import { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';
import { toast } from 'sonner';
import { Trash2, Edit2, Plus, X, ChevronRight, ChevronDown, ChevronUp, Package, Truck, MapPin, User, Clock, CreditCard, Download, FileSpreadsheet, FileText, Calendar, Users, Tag, Filter, HelpCircle, Gift, Star, MessageSquare, Search, Send, Phone, Mail, XCircle, BarChart3, ShoppingBag, RotateCcw, RefreshCw, Eye, Edit, Instagram } from 'lucide-react';

const ORDER_STATUSES = ['confirmed', 'processing', 'shipped', 'out_for_delivery', 'delivered', 'cancelled',
  'return_requested', 'return_approved', 'return_picked', 'returned',
  'exchange_requested', 'exchange_approved', 'exchange_shipped', 'exchange_delivered',
  'return_cancelled', 'exchange_cancelled'];

// Flow-aware status progressions
const NORMAL_FLOW = ['confirmed', 'processing', 'shipped', 'out_for_delivery', 'delivered'];
const RETURN_FLOW = ['return_requested', 'return_approved', 'return_picked', 'returned'];
const EXCHANGE_FLOW = ['exchange_requested', 'exchange_approved', 'exchange_shipped', 'exchange_delivered'];
const TERMINAL_STATES = ['delivered', 'cancelled', 'returned', 'exchange_delivered', 'return_cancelled', 'exchange_cancelled'];

const getActiveFlow = (status) => {
  if (RETURN_FLOW.includes(status)) return RETURN_FLOW;
  if (EXCHANGE_FLOW.includes(status)) return EXCHANGE_FLOW;
  return NORMAL_FLOW;
};

const getNextStatus = (status) => {
  const flow = getActiveFlow(status);
  const idx = flow.indexOf(status);
  if (idx >= 0 && idx < flow.length - 1) return flow[idx + 1];
  return null;
};

const STATUS_COLORS = {
  payment_pending: 'bg-yellow-100 text-yellow-800',
  payment_failed: 'bg-red-200 text-red-900',
  confirmed: 'bg-blue-100 text-blue-800',
  processing: 'bg-yellow-100 text-yellow-800',
  shipped: 'bg-purple-100 text-purple-800',
  out_for_delivery: 'bg-orange-100 text-orange-800',
  delivered: 'bg-green-100 text-green-800',
  cancelled: 'bg-red-100 text-red-800',
  return_requested: 'bg-red-100 text-red-800',
  return_approved: 'bg-orange-100 text-orange-800',
  return_picked: 'bg-orange-100 text-orange-800',
  returned: 'bg-gray-100 text-gray-800',
  exchange_requested: 'bg-red-100 text-red-800',
  exchange_approved: 'bg-orange-100 text-orange-800',
  exchange_shipped: 'bg-purple-100 text-purple-800',
  exchange_delivered: 'bg-green-100 text-green-800',
  return_cancelled: 'bg-gray-200 text-gray-700',
  exchange_cancelled: 'bg-gray-200 text-gray-700',
};

const STATUS_LABELS = {
  payment_pending: 'Payment Pending',
  payment_failed: 'Payment Failed',
  confirmed: 'Confirmed',
  processing: 'Processing',
  shipped: 'Shipped',
  out_for_delivery: 'Out for Delivery',
  delivered: 'Delivered',
  cancelled: 'Cancelled',
  return_requested: 'Return Requested',
  return_approved: 'Return Approved',
  return_picked: 'Return Picked Up',
  returned: 'Returned',
  exchange_requested: 'Exchange Requested',
  exchange_approved: 'Exchange Approved',
  exchange_shipped: 'Exchange Shipped',
  exchange_delivered: 'Exchange Delivered',
  return_cancelled: 'Return Cancelled',
  exchange_cancelled: 'Exchange Cancelled',
};

// ========== Product Form Component ==========
const ProductForm = ({ product, onSave, onCancel, categories = [], allProducts = [] }) => {
  const [form, setForm] = useState({
    name: product?.name || '',
    category: product?.category || 'saree',
    price: product?.price || '',
    original_price: product?.original_price || '',
    description: product?.description || '',
    story: product?.story || '',
    fabric: product?.fabric || '',
    color: product?.color || '',
    occasion: product?.occasion || '',
    images: product?.images?.join('\n') || '',
    video_url: product?.video_url || '',
    stock: product?.stock ?? 10,
    featured: product?.featured || false,
    color_variants: product?.color_variants || [],
  });
  const [saving, setSaving] = useState(false);
  const [variantSearch, setVariantSearch] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.price || !form.fabric || !form.color || !form.occasion || !form.images.trim()) {
      toast.error('Please fill all required fields');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        ...form,
        price: parseFloat(form.price),
        original_price: form.original_price ? parseFloat(form.original_price) : null,
        stock: parseInt(form.stock),
        images: form.images.split('\n').map(s => s.trim()).filter(Boolean),
        video_url: form.video_url?.trim() || null,
      };

      if (product?.product_id) {
        await api.put(`/products/${product.product_id}`, payload);
        toast.success('Product updated');
      } else {
        await api.post('/products', payload);
        toast.success('Product created');
      }
      onSave();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save product');
    } finally {
      setSaving(false);
    }
  };

  const addColorVariant = (p) => {
    if (form.color_variants.some(v => v.product_id === p.product_id)) return;
    setForm({ ...form, color_variants: [...form.color_variants, { product_id: p.product_id, color_name: p.color, product_name: p.name }] });
  };

  const removeColorVariant = (productId) => {
    setForm({ ...form, color_variants: form.color_variants.filter(v => v.product_id !== productId) });
  };

  const filteredForVariants = variantSearch.trim()
    ? allProducts.filter(p => p.product_id !== product?.product_id && (p.name.toLowerCase().includes(variantSearch.toLowerCase()) || p.color.toLowerCase().includes(variantSearch.toLowerCase())))
    : [];

  return (
    <div className="bg-neutral-warm p-6 rounded-sm mb-8 border border-gold/30" data-testid="product-form">
      <div className="flex justify-between items-center mb-6">
        <h3 className="font-heading text-2xl text-forest">
          {product?.product_id ? 'Edit Product' : 'Add New Product'}
        </h3>
        <button onClick={onCancel} className="p-2 text-forest hover:text-gold transition-colors" data-testid="product-form-cancel">
          <X className="w-5 h-5" />
        </button>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="text-xs uppercase tracking-[0.1em] text-forest">Name *</label>
            <input
              type="text" required value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="mt-1 w-full px-4 py-3 bg-ivory border border-gold/50 rounded-sm focus:outline-none focus:ring-2 focus:ring-forest"
              data-testid="product-name-input"
            />
          </div>
          <div>
            <label className="text-xs uppercase tracking-[0.1em] text-forest">Category *</label>
            <select
              value={form.category}
              onChange={(e) => setForm({ ...form, category: e.target.value })}
              className="mt-1 w-full px-4 py-3 bg-ivory border border-gold/50 rounded-sm focus:outline-none focus:ring-2 focus:ring-forest"
              data-testid="product-category-select"
            >
              <option value="saree">Saree</option>
              <option value="suit">Suit</option>
              {categories.filter(c => !['saree','suit'].includes(c.slug)).map(c => (
                <option key={c.category_id} value={c.slug}>{c.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-xs uppercase tracking-[0.1em] text-forest">Selling Price (₹, incl. 5% GST) *</label>
            <input
              type="number" required min="0" step="1" value={form.price}
              onChange={(e) => setForm({ ...form, price: e.target.value })}
              className="mt-1 w-full px-4 py-3 bg-ivory border border-gold/50 rounded-sm focus:outline-none focus:ring-2 focus:ring-forest"
              data-testid="product-price-input"
            />
          </div>
          <div>
            <label className="text-xs uppercase tracking-[0.1em] text-forest">MRP / Original Price (₹, for strike-through)</label>
            <input
              type="number" min="0" step="1" value={form.original_price}
              onChange={(e) => setForm({ ...form, original_price: e.target.value })}
              placeholder="Leave empty if no discount"
              className="mt-1 w-full px-4 py-3 bg-ivory border border-gold/50 rounded-sm focus:outline-none focus:ring-2 focus:ring-forest"
              data-testid="product-original-price-input"
            />
            {form.original_price && form.price && parseFloat(form.original_price) > parseFloat(form.price) && (
              <p className="text-xs text-green-600 mt-1">Discount: {Math.round((1 - parseFloat(form.price) / parseFloat(form.original_price)) * 100)}% off</p>
            )}
          </div>
          <div>
            <label className="text-xs uppercase tracking-[0.1em] text-forest">Stock</label>
            <input
              type="number" min="0" value={form.stock}
              onChange={(e) => setForm({ ...form, stock: e.target.value })}
              className="mt-1 w-full px-4 py-3 bg-ivory border border-gold/50 rounded-sm focus:outline-none focus:ring-2 focus:ring-forest"
              data-testid="product-stock-input"
            />
          </div>
          <div>
            <label className="text-xs uppercase tracking-[0.1em] text-forest">Fabric *</label>
            <input
              type="text" required value={form.fabric}
              onChange={(e) => setForm({ ...form, fabric: e.target.value })}
              placeholder="e.g. Pure Silk, Cotton, Georgette"
              className="mt-1 w-full px-4 py-3 bg-ivory border border-gold/50 rounded-sm focus:outline-none focus:ring-2 focus:ring-forest"
              data-testid="product-fabric-input"
            />
          </div>
          <div>
            <label className="text-xs uppercase tracking-[0.1em] text-forest">Color *</label>
            <input
              type="text" required value={form.color}
              onChange={(e) => setForm({ ...form, color: e.target.value })}
              placeholder="e.g. Red, Emerald Green, Royal Blue"
              className="mt-1 w-full px-4 py-3 bg-ivory border border-gold/50 rounded-sm focus:outline-none focus:ring-2 focus:ring-forest"
              data-testid="product-color-input"
            />
          </div>
          <div>
            <label className="text-xs uppercase tracking-[0.1em] text-forest">Occasion *</label>
            <input
              type="text" required value={form.occasion}
              onChange={(e) => setForm({ ...form, occasion: e.target.value })}
              placeholder="e.g. Wedding, Festival, Casual, Party"
              className="mt-1 w-full px-4 py-3 bg-ivory border border-gold/50 rounded-sm focus:outline-none focus:ring-2 focus:ring-forest"
              data-testid="product-occasion-input"
            />
          </div>
          <div className="flex items-end pb-1">
            <label className="flex items-center space-x-3 cursor-pointer">
              <input
                type="checkbox" checked={form.featured}
                onChange={(e) => setForm({ ...form, featured: e.target.checked })}
                className="w-4 h-4 accent-forest"
                data-testid="product-featured-checkbox"
              />
              <span className="text-sm text-forest">Featured Product</span>
            </label>
          </div>
        </div>

        <div>
          <label className="text-xs uppercase tracking-[0.1em] text-forest">Description *</label>
          <textarea
            required value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
            rows={3}
            className="mt-1 w-full px-4 py-3 bg-ivory border border-gold/50 rounded-sm focus:outline-none focus:ring-2 focus:ring-forest resize-none"
            data-testid="product-description-input"
          />
        </div>

        <div>
          <label className="text-xs uppercase tracking-[0.1em] text-forest">Story (optional)</label>
          <textarea
            value={form.story}
            onChange={(e) => setForm({ ...form, story: e.target.value })}
            rows={2}
            placeholder="The story behind this piece..."
            className="mt-1 w-full px-4 py-3 bg-ivory border border-gold/50 rounded-sm focus:outline-none focus:ring-2 focus:ring-forest resize-none"
            data-testid="product-story-input"
          />
        </div>

        <div>
          <label className="text-xs uppercase tracking-[0.1em] text-forest">Image URLs * (one per line)</label>
          <textarea
            required value={form.images}
            onChange={(e) => setForm({ ...form, images: e.target.value })}
            rows={3}
            placeholder={"https://example.com/image1.jpg\nhttps://drive.google.com/file/d/.../view"}
            className="mt-1 w-full px-4 py-3 bg-ivory border border-gold/50 rounded-sm focus:outline-none focus:ring-2 focus:ring-forest resize-none font-mono text-sm"
            data-testid="product-images-input"
          />
          <p className="text-xs text-muted-foreground mt-1">Supports: Direct image URLs, Google Drive share links, Unsplash, Imgur, Cloudinary</p>
        </div>

        <div>
          <label className="text-xs uppercase tracking-[0.1em] text-forest">Product Video URL (YouTube or Google Drive)</label>
          <input
            type="text" value={form.video_url}
            onChange={(e) => setForm({ ...form, video_url: e.target.value })}
            placeholder="https://www.youtube.com/watch?v=... or Google Drive video link"
            className="mt-1 w-full px-4 py-3 bg-ivory border border-gold/50 rounded-sm focus:outline-none focus:ring-2 focus:ring-forest text-sm"
            data-testid="product-video-input"
          />
        </div>

        {/* Color Variants */}
        <div>
          <label className="text-xs uppercase tracking-[0.1em] text-forest mb-2 block">Color Variants (link same product in other colors)</label>
          {form.color_variants.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-2">
              {form.color_variants.map(v => (
                <span key={v.product_id} className="inline-flex items-center gap-1 px-3 py-1 bg-ivory border border-gold/30 text-xs text-forest">
                  {v.color_name} — {v.product_name?.slice(0, 25)}
                  <button type="button" onClick={() => removeColorVariant(v.product_id)} className="text-red-400 hover:text-red-600 ml-1"><X className="w-3 h-3" /></button>
                </span>
              ))}
            </div>
          )}
          <input
            type="text"
            value={variantSearch}
            onChange={e => setVariantSearch(e.target.value)}
            placeholder="Search products to add as color variant..."
            className="w-full px-4 py-2 bg-ivory border border-gold/50 rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-forest"
            data-testid="color-variant-search"
          />
          {filteredForVariants.length > 0 && (
            <div className="border border-gold/30 bg-ivory max-h-32 overflow-y-auto mt-1">
              {filteredForVariants.slice(0, 8).map(p => (
                <button key={p.product_id} type="button" onClick={() => { addColorVariant(p); setVariantSearch(''); }}
                  className="w-full text-left px-3 py-2 text-xs hover:bg-gold/10 border-b border-gold/10 last:border-0">
                  <span className="text-forest font-medium">{p.name}</span>
                  <span className="text-muted-foreground ml-2">({p.color})</span>
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="flex space-x-3 pt-2">
          <button
            type="submit" disabled={saving}
            className="bg-forest text-ivory px-8 py-3 rounded-none hover:bg-gold hover:text-forest transition-colors text-sm uppercase tracking-[0.1em] disabled:opacity-50"
            data-testid="product-form-submit"
          >
            {saving ? 'Saving...' : (product?.product_id ? 'Update Product' : 'Create Product')}
          </button>
          <button
            type="button" onClick={onCancel}
            className="border border-forest text-forest px-8 py-3 rounded-none hover:bg-forest hover:text-ivory transition-colors text-sm uppercase tracking-[0.1em]"
          >
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
};

// ========== Order Detail Component ==========
const OrderDetail = ({ order, onBack, onStatusUpdate, onDownloadInvoice }) => {
  const [updating, setUpdating] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [showCancelReturnModal, setShowCancelReturnModal] = useState(false);
  const [cancelReturnReason, setCancelReturnReason] = useState('');
  const addr = order.shipping_address || {};
  const customer = order.customer_info || {};
  const currentStatus = order.order_status || 'confirmed';
  const currentIdx = ORDER_STATUSES.indexOf(currentStatus);

  const handleStatusChange = async (newStatus) => {
    if (newStatus === currentStatus) return;
    setUpdating(true);
    try {
      const response = await api.put(`/admin/orders/${order.order_id}`, { order_status: newStatus });
      toast.success(`Order status updated to ${STATUS_LABELS[newStatus]}`);
      onStatusUpdate(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update status');
    } finally {
      setUpdating(false);
    }
  };

  const handleAdminCancel = async () => {
    if (!cancelReason.trim()) { toast.error('Please provide a reason for cancellation'); return; }
    setUpdating(true);
    try {
      await api.post(`/admin/orders/${order.order_id}/cancel`, { reason: cancelReason.trim() });
      toast.success('Order cancelled and customer notified');
      onStatusUpdate({ ...order, order_status: 'cancelled', cancel_reason: cancelReason.trim() });
      setShowCancelModal(false);
      setCancelReason('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to cancel order');
    } finally {
      setUpdating(false);
    }
  };

  const handleCancelReturnExchange = async () => {
    if (!cancelReturnReason.trim()) { toast.error('Reason is required'); return; }
    setUpdating(true);
    try {
      await api.post(`/admin/orders/${order.order_id}/cancel-return-exchange`, { reason: cancelReturnReason.trim() });
      const newStatus = currentStatus.startsWith('return') ? 'return_cancelled' : 'exchange_cancelled';
      toast.success(`${currentStatus.startsWith('return') ? 'Return' : 'Exchange'} request cancelled`);
      onStatusUpdate({ ...order, order_status: newStatus, return_cancel_reason: cancelReturnReason.trim() });
      setShowCancelReturnModal(false);
      setCancelReturnReason('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to cancel return/exchange');
    } finally {
      setUpdating(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="order-detail-view">
      {/* Back button + header */}
      <button onClick={onBack} className="text-sm text-forest hover:text-gold transition-colors flex items-center" data-testid="order-detail-back">
        <ChevronRight className="w-4 h-4 mr-1 rotate-180" /> Back to Orders
      </button>

      <div className="flex flex-col sm:flex-row justify-between items-start gap-4">
        <div>
          <h2 className="font-heading text-2xl text-forest" data-testid="order-detail-id">Order #{order.order_id.slice(0, 8)}</h2>
          <p className="text-sm text-muted-foreground flex items-center mt-1">
            <Clock className="w-3.5 h-3.5 mr-1.5" />
            Placed on {new Date(order.created_at).toLocaleString()}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={() => onDownloadInvoice(order.order_id)}
            className="flex items-center space-x-1.5 border border-forest text-forest px-4 py-2 rounded-none hover:bg-forest hover:text-ivory transition-colors text-xs uppercase tracking-[0.1em]"
            data-testid="download-invoice-button"
          >
            <Download className="w-3.5 h-3.5" />
            <span>Invoice</span>
          </button>
          <span className={`px-4 py-1.5 rounded-full text-xs uppercase tracking-wider font-medium ${STATUS_COLORS[currentStatus] || 'bg-gray-100 text-gray-800'}`} data-testid="order-detail-status">
            {STATUS_LABELS[currentStatus] || currentStatus}
          </span>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left column: Billing + Delivery + Payment */}
        <div className="space-y-6">
          {/* Billing / Account Info */}
          <div className="bg-neutral-warm p-5 rounded-sm" data-testid="order-billing-info">
            <h4 className="text-xs uppercase tracking-[0.2em] text-gold mb-3 flex items-center">
              <User className="w-3.5 h-3.5 mr-1.5" /> Billing / Account
            </h4>
            <div className="space-y-1.5 text-sm">
              {(() => {
                const billing = order.billing_info || customer;
                return (
                  <>
                    <p className="text-forest font-medium">{billing.name || order.guest_name || 'N/A'}</p>
                    <p className="text-muted-foreground">{billing.email || order.guest_email || 'N/A'}</p>
                    {(billing.phone || order.guest_phone) && (
                      <p className="text-muted-foreground">{billing.phone || order.guest_phone}</p>
                    )}
                  </>
                );
              })()}
              <p className="text-xs text-muted-foreground mt-2">
                {order.user_id ? 'Registered User' : 'Guest Checkout'}
              </p>
              {order.gift_wrap && order.is_surprise_gift && (
                <p className="text-xs text-pink-700 mt-1 font-medium">Surprise gift — Hide sender details on packaging</p>
              )}
            </div>
          </div>

          {/* Delivery Address */}
          <div className="bg-neutral-warm p-5 rounded-sm" data-testid="order-delivery-address">
            <h4 className="text-xs uppercase tracking-[0.2em] text-gold mb-3 flex items-center">
              <MapPin className="w-3.5 h-3.5 mr-1.5" /> Delivery Address
            </h4>
            <div className="text-sm text-forest leading-relaxed">
              {addr.name && <p className="font-medium">{addr.name}</p>}
              {addr.address_line1 && <p>{addr.address_line1}</p>}
              {addr.address_line2 && <p>{addr.address_line2}</p>}
              {(!addr.address_line1 && addr.address) && <p>{addr.address}</p>}
              {addr.street && <p>{addr.street}</p>}
              {(addr.city || addr.state || addr.pincode || addr.zip) && (
                <p>{[addr.city, addr.state].filter(Boolean).join(', ')} {addr.pincode || addr.zip || ''}</p>
              )}
              {addr.phone && <p className="text-muted-foreground mt-1">Phone: {addr.phone}</p>}
              {(addr.lat && addr.lng) && (
                <a href={`https://www.google.com/maps?q=${addr.lat},${addr.lng}`}
                  target="_blank" rel="noopener noreferrer"
                  className="inline-flex items-center space-x-1.5 mt-2 px-3 py-1.5 bg-forest/10 border border-forest/30 text-forest text-xs rounded-sm hover:bg-forest hover:text-ivory transition-colors"
                  data-testid="admin-maps-directions">
                  <MapPin className="w-3 h-3" />
                  <span>View on Map</span>
                </a>
              )}
              {(!addr.lat && (addr.city || addr.pincode)) && (
                <div className="mt-2 space-y-1">
                  <a href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent([addr.address_line1 || addr.address, addr.city, addr.state, addr.pincode].filter(Boolean).join(', '))}`}
                    target="_blank" rel="noopener noreferrer"
                    className="inline-flex items-center space-x-1.5 px-3 py-1.5 bg-forest/10 border border-forest/30 text-forest text-xs rounded-sm hover:bg-forest hover:text-ivory transition-colors"
                    data-testid="admin-maps-search">
                    <MapPin className="w-3 h-3" />
                    <span>Search on Maps</span>
                  </a>
                  <p className="text-[10px] text-amber-600 flex items-center space-x-1"><span>Approximate location — exact coordinates not saved</span></p>
                </div>
              )}
              {!addr.city && !addr.street && !addr.address_line1 && Object.keys(addr).length > 0 && (
                <div className="text-muted-foreground">
                  {Object.entries(addr).map(([key, val]) => (
                    <p key={key}><span className="capitalize">{key.replace(/_/g, ' ')}:</span> {String(val)}</p>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Payment */}
          <div className="bg-neutral-warm p-5 rounded-sm" data-testid="order-payment-info">
            <h4 className="text-xs uppercase tracking-[0.2em] text-gold mb-3 flex items-center">
              <CreditCard className="w-3.5 h-3.5 mr-1.5" /> Payment
            </h4>
            <div className="text-sm space-y-1.5">
              <p className="text-forest">Status: <span className="font-medium capitalize">{order.payment_status}</span></p>
              {order.payment_id && <p className="text-muted-foreground">Payment ID: {order.payment_id}</p>}
              {order.razorpay_order_id && <p className="text-muted-foreground">Razorpay: {order.razorpay_order_id}</p>}
            </div>
          </div>

          {/* Gift Packaging */}
          {order.gift_wrap && (
            <div className="bg-pink-50 p-5 rounded-sm border border-pink-200" data-testid="order-gift-info">
              <h4 className="text-xs uppercase tracking-[0.2em] text-pink-700 mb-3 flex items-center">
                <Gift className="w-3.5 h-3.5 mr-1.5" /> Gift Packaging (+₹100)
              </h4>
              <div className="text-sm space-y-1.5">
                {order.gift_message && <p className="text-forest italic">"{order.gift_message}"</p>}
                {order.is_surprise_gift && (
                  <p className="text-pink-700 font-medium text-xs uppercase tracking-wider mt-2">Surprise Gift — Hide sender address on packaging</p>
                )}
              </div>
            </div>
          )}

          {/* Return / Exchange Info */}
          {order.return_exchange_type && (
            <div className="bg-red-50 p-5 rounded-sm border border-red-200" data-testid="order-return-info">
              <h4 className="text-xs uppercase tracking-[0.2em] text-red-700 mb-3">
                {order.return_exchange_type === 'return' ? 'Return' : 'Exchange'} Request
              </h4>
              <div className="text-sm space-y-1.5">
                <p className="text-forest"><strong>Reason:</strong> {order.return_exchange_reason}</p>
                {order.return_requested_at && (
                  <p className="text-muted-foreground text-xs">Requested: {new Date(order.return_requested_at).toLocaleString()}</p>
                )}
              </div>
              {/* Return/Exchange Media */}
              {order.return_exchange_media?.length > 0 && (
                <div className="mt-3 pt-3 border-t border-red-200">
                  <p className="text-xs uppercase tracking-wider text-red-700 mb-2">Attached Evidence</p>
                  <div className="flex flex-wrap gap-2">
                    {order.return_exchange_media.map((url, i) => (
                      <a key={i} href={url} target="_blank" rel="noopener noreferrer" className="block">
                        {url.includes('.mp4') || url.includes('.mov') ? (
                          <video src={url} className="w-20 h-20 object-cover rounded-sm border border-red-200" />
                        ) : (
                          <img src={url} alt="Evidence" className="w-20 h-20 object-cover rounded-sm border border-red-200" />
                        )}
                      </a>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Middle column: Order Items */}
        <div className="bg-neutral-warm p-5 rounded-sm" data-testid="order-items-list">
          <h4 className="text-xs uppercase tracking-[0.2em] text-gold mb-4 flex items-center">
            <Package className="w-3.5 h-3.5 mr-1.5" /> Items
          </h4>
          <div className="space-y-4">
            {order.items.map((item, idx) => (
              <div key={idx} className="flex space-x-3" data-testid="order-detail-item">
                <img src={item.image} alt={item.name} className="w-16 h-20 object-cover rounded-sm flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-forest font-medium truncate">{item.name}</p>
                  <p className="text-xs text-muted-foreground">Qty: {item.quantity}</p>
                  <p className="text-sm text-forest">₹{(item.price * item.quantity).toLocaleString()}</p>
                </div>
              </div>
            ))}
          </div>
          <div className="border-t border-gold/30 mt-4 pt-4 space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Subtotal</span>
              <span className="text-forest">₹{order.items.reduce((s, i) => s + i.price * i.quantity, 0).toLocaleString()}</span>
            </div>
            {order.coupon_code && (
              <div className="flex justify-between text-sm text-green-700" data-testid="order-detail-coupon">
                <span>Discount ({order.coupon_code})</span>
                <span>- ₹{(order.discount_amount || 0).toLocaleString()}</span>
              </div>
            )}
            {order.gift_wrap && (
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Gift Packaging</span>
                <span className="text-forest">+ ₹100</span>
              </div>
            )}
            {order.gst_data && (
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">GST</span>
                <span className="text-forest">+ ₹{(order.gst_data.total_gst || 0).toLocaleString()}</span>
              </div>
            )}
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Delivery</span>
              <span className="text-green-600">Free</span>
            </div>
            <div className="flex justify-between text-lg pt-2 border-t border-gold/20">
              <span className="font-heading text-forest">Total Paid</span>
              <span className="font-heading text-forest" data-testid="order-detail-total">₹{order.total_amount.toLocaleString()}</span>
            </div>
          </div>
        </div>

        {/* Right column: Status Management */}
        <div className="space-y-6">
          <div className="bg-neutral-warm p-5 rounded-sm" data-testid="order-status-management">
            <h4 className="text-xs uppercase tracking-[0.2em] text-gold mb-4 flex items-center">
              <Truck className="w-3.5 h-3.5 mr-1.5" /> Update Status
            </h4>

            {/* Status Timeline - Flow Aware */}
            <div className="space-y-0">
              {(() => {
                const flow = getActiveFlow(currentStatus);
                // For return/exchange flows, show normal flow up to delivered first, then the specific flow
                const showNormalPrefix = flow !== NORMAL_FLOW;
                const timelineStatuses = showNormalPrefix ? [...NORMAL_FLOW, ...flow] : NORMAL_FLOW;
                const currentFlowIdx = flow.indexOf(currentStatus);

                return timelineStatuses.map((status, idx) => {
                  let isActive = false;
                  let isCurrent = status === currentStatus;

                  if (flow === NORMAL_FLOW) {
                    isActive = idx <= NORMAL_FLOW.indexOf(currentStatus) && currentStatus !== 'cancelled';
                  } else {
                    // Normal flow part is fully completed
                    if (idx < NORMAL_FLOW.length) {
                      isActive = true;
                    } else {
                      // Within the return/exchange flow
                      const flowIdx = idx - NORMAL_FLOW.length;
                      isActive = flowIdx <= currentFlowIdx;
                    }
                  }

                  return (
                    <div key={status} className="flex items-start">
                      <div className="flex flex-col items-center mr-3">
                        <div className={`w-3 h-3 rounded-full border-2 ${
                          isActive ? 'bg-forest border-forest' : 'bg-transparent border-gold/40'
                        } ${isCurrent ? 'ring-2 ring-forest/30 ring-offset-1' : ''}`}></div>
                        {idx < timelineStatuses.length - 1 && (
                          <div className={`w-0.5 h-6 ${isActive ? 'bg-forest' : 'bg-gold/20'}`}></div>
                        )}
                      </div>
                      <span className={`text-sm pb-4 ${isCurrent ? 'text-forest font-medium' : isActive ? 'text-forest/70' : 'text-muted-foreground'}`}>
                        {STATUS_LABELS[status]}
                      </span>
                    </div>
                  );
                });
              })()}
            </div>

            {/* Action Buttons - Flow Aware */}
            {!TERMINAL_STATES.includes(currentStatus) && currentStatus !== 'cancelled' && (
              <div className="space-y-2 mt-4 pt-4 border-t border-gold/20">
                {/* Next status button within the current flow */}
                {getNextStatus(currentStatus) && (
                  <button
                    onClick={() => handleStatusChange(getNextStatus(currentStatus))}
                    disabled={updating}
                    className="w-full bg-forest text-ivory px-4 py-3 rounded-none hover:bg-gold hover:text-forest transition-colors text-sm uppercase tracking-[0.1em] disabled:opacity-50"
                    data-testid="order-next-status-button"
                  >
                    {updating ? 'Updating...' : `Mark as ${STATUS_LABELS[getNextStatus(currentStatus)]}`}
                  </button>
                )}
                {/* Cancel button — only for normal flow (not return/exchange) */}
                {NORMAL_FLOW.includes(currentStatus) && (
                  <button
                    onClick={() => setShowCancelModal(true)}
                    disabled={updating}
                    className="w-full border border-red-400 text-red-600 px-4 py-3 rounded-none hover:bg-red-50 transition-colors text-sm uppercase tracking-[0.1em] disabled:opacity-50"
                    data-testid="order-cancel-button"
                  >
                    Cancel Order
                  </button>
                )}
                {/* Cancel Return/Exchange button — for return/exchange flows */}
                {(RETURN_FLOW.includes(currentStatus) || EXCHANGE_FLOW.includes(currentStatus)) && (
                  <button
                    onClick={() => setShowCancelReturnModal(true)}
                    disabled={updating}
                    className="w-full border border-red-400 text-red-600 px-4 py-3 rounded-none hover:bg-red-50 transition-colors text-sm uppercase tracking-[0.1em] disabled:opacity-50"
                    data-testid="cancel-return-exchange-button"
                  >
                    <XCircle className="w-4 h-4 inline mr-1.5" />
                    Cancel {currentStatus.startsWith('return') ? 'Return' : 'Exchange'}
                  </button>
                )}
              </div>
            )}

            {currentStatus === 'delivered' && (
              <p className="text-sm text-green-700 text-center mt-4 pt-4 border-t border-gold/20">This order has been delivered. Awaiting customer feedback.</p>
            )}
            {currentStatus === 'cancelled' && (
              <p className="text-sm text-red-600 text-center mt-4 pt-4 border-t border-gold/20">This order has been cancelled.{order.cancel_reason && ` Reason: ${order.cancel_reason}`}</p>
            )}
            {currentStatus === 'returned' && (
              <p className="text-sm text-forest text-center mt-4 pt-4 border-t border-gold/20">Return completed. Refund will be processed.</p>
            )}
            {currentStatus === 'exchange_delivered' && (
              <p className="text-sm text-green-700 text-center mt-4 pt-4 border-t border-gold/20">Exchange delivered successfully.</p>
            )}
            {(currentStatus === 'return_cancelled' || currentStatus === 'exchange_cancelled') && (
              <div className="text-center mt-4 pt-4 border-t border-gold/20">
                <p className="text-sm text-gray-700">{currentStatus === 'return_cancelled' ? 'Return' : 'Exchange'} request has been cancelled by admin.</p>
                {order.return_cancel_reason && <p className="text-xs text-muted-foreground mt-1">Reason: {order.return_cancel_reason}</p>}
              </div>
            )}

            {/* Cancel Reason Modal */}
            {showCancelModal && (
              <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" data-testid="cancel-modal">
                <div className="bg-ivory w-full max-w-md p-6 space-y-4">
                  <h3 className="font-heading text-xl text-forest">Cancel Order</h3>
                  <p className="text-sm text-forest/60">Please provide a reason for cancellation. The customer will be notified via email.</p>
                  <textarea
                    value={cancelReason} onChange={(e) => setCancelReason(e.target.value)}
                    placeholder="Reason for cancellation..."
                    rows={3}
                    className="w-full px-4 py-3 bg-transparent border border-forest/20 text-sm text-forest focus:outline-none focus:border-gold resize-none"
                    data-testid="cancel-reason-input"
                    autoFocus
                  />
                  <div className="flex gap-3">
                    <button onClick={() => { setShowCancelModal(false); setCancelReason(''); }}
                      className="flex-1 border border-forest/20 text-forest py-2.5 text-sm uppercase tracking-[0.1em] hover:bg-forest/5 transition-colors">
                      Go Back
                    </button>
                    <button onClick={handleAdminCancel} disabled={updating || !cancelReason.trim()}
                      className="flex-1 bg-red-600 text-white py-2.5 text-sm uppercase tracking-[0.1em] hover:bg-red-700 transition-colors disabled:opacity-50"
                      data-testid="confirm-cancel-button">
                      {updating ? 'Cancelling...' : 'Confirm Cancel'}
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Cancel Return/Exchange Modal */}
            {showCancelReturnModal && (
              <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" data-testid="cancel-return-modal">
                <div className="bg-ivory w-full max-w-md p-6 space-y-4">
                  <h3 className="font-heading text-xl text-forest">
                    Cancel {(order.order_status || '').startsWith('return') ? 'Return' : 'Exchange'} Request
                  </h3>
                  <p className="text-sm text-forest/60">Provide a reason for rejecting this {(order.order_status || '').startsWith('return') ? 'return' : 'exchange'} request.</p>
                  <textarea
                    value={cancelReturnReason} onChange={(e) => setCancelReturnReason(e.target.value)}
                    placeholder="e.g. No defect found, product misuse, false claim..."
                    rows={3}
                    className="w-full px-4 py-3 bg-transparent border border-forest/20 text-sm text-forest focus:outline-none focus:border-gold resize-none"
                    data-testid="cancel-return-reason-input"
                    autoFocus
                  />
                  <div className="flex gap-3">
                    <button onClick={() => { setShowCancelReturnModal(false); setCancelReturnReason(''); }}
                      className="flex-1 border border-forest/20 text-forest py-2.5 text-sm uppercase tracking-[0.1em] hover:bg-forest/5 transition-colors">
                      Go Back
                    </button>
                    <button onClick={handleCancelReturnExchange} disabled={!cancelReturnReason.trim() || updating}
                      className="flex-1 bg-red-600 text-white py-2.5 text-sm uppercase tracking-[0.1em] hover:bg-red-700 transition-colors disabled:opacity-50"
                      data-testid="confirm-cancel-return-button">
                      {updating ? 'Cancelling...' : 'Confirm Cancellation'}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// ========== Main Admin Component ==========
const Admin = () => {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('products');
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [showProductForm, setShowProductForm] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [loadingOrder, setLoadingOrder] = useState(false);
  const [exportPeriod, setExportPeriod] = useState('month');
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  const [exporting, setExporting] = useState(false);
  const [users, setUsers] = useState([]);
  const [showUserForm, setShowUserForm] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [userForm, setUserForm] = useState({ name: '', email: '', phone: '', password: '', is_admin: false });
  const [coupons, setCoupons] = useState([]);
  const [showCouponForm, setShowCouponForm] = useState(false);
  const [couponForm, setCouponForm] = useState({
    code: '', discount_type: 'percentage', discount_value: '', min_order: '', max_discount: '', valid_until: '', usage_limit: 100, is_active: true
  });
  // Support Tickets state
  const [tickets, setTickets] = useState([]);
  const [ticketFilter, setTicketFilter] = useState('all'); // all, open, resolved
  const [resolvingTicket, setResolvingTicket] = useState(null);
  const [adminNote, setAdminNote] = useState('');
  // Order Filters state
  const [orderStatusFilter, setOrderStatusFilter] = useState('all');
  const [orderSearch, setOrderSearch] = useState('');
  // Reviews state
  const [allReviews, setAllReviews] = useState([]);
  const [reviewSortBy, setReviewSortBy] = useState('recent');
  // Search/filter states for all tabs
  const [productSearch, setProductSearch] = useState('');
  const [productCategoryFilter, setProductCategoryFilter] = useState('all');
  const [userSearch, setUserSearch] = useState('');
  const [couponSearch, setCouponSearch] = useState('');
  const [couponStatusFilter, setCouponStatusFilter] = useState('all');
  const [ticketSearch, setTicketSearch] = useState('');
  const [reviewSearch, setReviewSearch] = useState('');
  // Product analytics
  const [productAnalytics, setProductAnalytics] = useState({});
  const [expandedProduct, setExpandedProduct] = useState(null);
  // Ticket chat
  const [expandedTicket, setExpandedTicket] = useState(null);
  const [ticketMessage, setTicketMessage] = useState('');
  // Categories state
  const [categories, setCategories] = useState([]);
  const [showCategoryForm, setShowCategoryForm] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [categoryForm, setCategoryForm] = useState({ name: '', slug: '', description: '', image: '', is_active: true, display_order: 0 });
  // Banners state
  const [banners, setBanners] = useState([]);
  const [showBannerForm, setShowBannerForm] = useState(false);
  const [editingBanner, setEditingBanner] = useState(null);
  const [bannerForm, setBannerForm] = useState({ title: '', subtitle: '', image_url: '', link_url: '', bg_color: '#2D4A3E', text_color: '#FFFFFF', is_active: true, display_order: 0, pages: ['home'] });
  // Stories state
  const [stories, setStories] = useState([]);
  const [showStoryForm, setShowStoryForm] = useState(false);
  const [editingStory, setEditingStory] = useState(null);
  const [storyForm, setStoryForm] = useState({ title: '', excerpt: '', content: '', author: 'Vastram', image_url: '', tags: '', is_published: true });
  // Instagram state
  const [instagramPosts, setInstagramPosts] = useState([]);
  const [showInstaForm, setShowInstaForm] = useState(false);
  const [editingInstaPost, setEditingInstaPost] = useState(null);
  const [instaForm, setInstaForm] = useState({ media_type: 'image', image_url: '', youtube_url: '', caption: '', instagram_url: '', display_order: 0, is_active: true });
  // Site Settings state
  const [siteSettings, setSiteSettings] = useState({});
  const [settingsForm, setSettingsForm] = useState({ hero_image: '', saree_section_image: '', suit_section_image: '', our_story_image: '' });
  const [savingSettings, setSavingSettings] = useState(false);
  useEffect(() => {
    if (authLoading) return; // Wait for auth to resolve
    if (!user?.is_admin) {
      navigate('/');
      toast.error('Admin access required');
      return;
    }
    fetchData();
  }, [user, authLoading, activeTab]);

  const fetchData = async () => {
    try {
      if (activeTab === 'products') {
        const response = await api.get('/products');
        setProducts(response.data);
        try { const catRes = await api.get('/categories/all'); setCategories(catRes.data); } catch {}
      } else if (activeTab === 'orders') {
        const response = await api.get('/admin/orders');
        setOrders(response.data);
      } else if (activeTab === 'users') {
        const response = await api.get('/admin/users');
        setUsers(response.data);
      } else if (activeTab === 'coupons') {
        const response = await api.get('/admin/coupons');
        setCoupons(response.data);
      } else if (activeTab === 'tickets') {
        const response = await api.get('/admin/support/tickets');
        setTickets(response.data);
      } else if (activeTab === 'reviews') {
        const response = await api.get('/admin/reviews');
        setAllReviews(response.data);
      } else if (activeTab === 'categories') {
        const response = await api.get('/categories/all');
        setCategories(response.data);
      } else if (activeTab === 'banners') {
        const response = await api.get('/banners/all');
        setBanners(response.data);
      } else if (activeTab === 'stories') {
        const response = await api.get('/admin/drape-stories');
        setStories(response.data);
      } else if (activeTab === 'instagram') {
        const response = await api.get('/admin/instagram-posts');
        setInstagramPosts(response.data);
      } else if (activeTab === 'settings') {
        const response = await api.get('/site-settings');
        setSiteSettings(response.data);
        setSettingsForm({
          hero_image: response.data.hero_image || '',
          saree_section_image: response.data.saree_section_image || '',
          suit_section_image: response.data.suit_section_image || '',
          our_story_image: response.data.our_story_image || '',
        });
      }
      // Always load analytics when on products tab
      if (activeTab === 'products') {
        try {
          const aRes = await api.get('/admin/products/analytics');
          setProductAnalytics(aRes.data);
        } catch {}
      }
    } catch (error) {
      console.error('Failed to fetch data:', error);
    }
  };

  // User CRUD handlers
  const openUserForm = (u = null) => {
    if (u) {
      setEditingUser(u);
      setUserForm({ name: u.name || '', email: u.email || '', phone: u.phone || '', password: '', is_admin: u.is_admin || false });
    } else {
      setEditingUser(null);
      setUserForm({ name: '', email: '', phone: '', password: '', is_admin: false });
    }
    setShowUserForm(true);
  };

  const handleSaveUser = async (e) => {
    e.preventDefault();
    if (!userForm.name.trim() || !userForm.email.trim()) {
      toast.error('Name and email are required');
      return;
    }
    try {
      if (editingUser) {
        const payload = { name: userForm.name, email: userForm.email, phone: userForm.phone, is_admin: userForm.is_admin };
        if (userForm.password) payload.password = userForm.password;
        await api.put(`/admin/users/${editingUser.user_id}`, payload);
        toast.success('User updated');
      } else {
        if (!userForm.password) { toast.error('Password is required for new users'); return; }
        await api.post('/admin/users', userForm);
        toast.success('User created');
      }
      setShowUserForm(false);
      setEditingUser(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save user');
    }
  };

  const handleDeleteUser = async (userId, userName) => {
    if (!window.confirm(`Delete user "${userName}"? This will also remove their addresses, wishlist, and cart.`)) return;
    try {
      await api.delete(`/admin/users/${userId}`);
      toast.success('User deleted');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete user');
    }
  };


  // Support ticket handlers
  const handleUpdateTicket = async (ticketId, status) => {
    try {
      const payload = { status };
      if (status === 'resolved' && adminNote.trim()) payload.admin_note = adminNote.trim();
      await api.put(`/admin/support/tickets/${ticketId}`, payload);
      toast.success(`Ticket ${status === 'resolved' ? 'resolved' : 'reopened'}`);
      setResolvingTicket(null);
      setAdminNote('');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update ticket');
    }
  };

  // Send ticket chat message (admin)
  const handleSendTicketMessage = async (ticketId) => {
    if (!ticketMessage.trim()) return;
    try {
      await api.post(`/admin/support/tickets/${ticketId}/messages`, { text: ticketMessage });
      setTicketMessage('');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send message');
    }
  };

  // Filtered orders
  const filteredOrders = orders.filter(o => {
    if (orderStatusFilter !== 'all' && (o.order_status || 'confirmed') !== orderStatusFilter) return false;
    if (orderSearch.trim()) {
      const q = orderSearch.toLowerCase();
      const addr = o.shipping_address || {};
      const name = (o.guest_name || o.customer_info?.name || '').toLowerCase();
      const city = (addr.city || '').toLowerCase();
      const state = (addr.state || '').toLowerCase();
      const oid = (o.order_id || '').toLowerCase();
      if (!name.includes(q) && !city.includes(q) && !state.includes(q) && !oid.includes(q)) return false;
    }
    return true;
  });

  // Filtered tickets
  const filteredTickets = tickets.filter(t => {
    if (ticketFilter !== 'all' && t.status !== ticketFilter) return false;
    if (ticketSearch.trim()) {
      const q = ticketSearch.toLowerCase();
      if (!(t.title || '').toLowerCase().includes(q) && !(t.user_name || '').toLowerCase().includes(q) && !(t.user_email || '').toLowerCase().includes(q) && !(t.issue_tag || '').toLowerCase().includes(q)) return false;
    }
    return true;
  });

  // Filtered products
  const filteredProducts = products.filter(p => {
    if (productCategoryFilter !== 'all' && p.category !== productCategoryFilter) return false;
    if (productSearch.trim()) {
      const q = productSearch.toLowerCase();
      if (!p.name.toLowerCase().includes(q) && !(p.fabric || '').toLowerCase().includes(q) && !(p.color || '').toLowerCase().includes(q)) return false;
    }
    return true;
  });
  const productCategories = [...new Set(products.map(p => p.category).filter(Boolean))];

  // Filtered users
  const filteredUsers = users.filter(u => {
    if (!userSearch.trim()) return true;
    const q = userSearch.toLowerCase();
    return (u.name || '').toLowerCase().includes(q) || (u.email || '').toLowerCase().includes(q) || (u.phone || '').toLowerCase().includes(q);
  });

  // Filtered coupons
  const filteredCoupons = coupons.filter(c => {
    if (couponStatusFilter === 'active' && !c.is_active) return false;
    if (couponStatusFilter === 'inactive' && c.is_active) return false;
    if (couponSearch.trim()) {
      const q = couponSearch.toLowerCase();
      if (!c.code.toLowerCase().includes(q)) return false;
    }
    return true;
  });

  // Filtered reviews
  const filteredReviewsList = allReviews.filter(r => {
    if (!reviewSearch.trim()) return true;
    const q = reviewSearch.toLowerCase();
    return (r.product_name || '').toLowerCase().includes(q) || (r.user_name || '').toLowerCase().includes(q);
  });


  const handleDeleteProduct = async (productId) => {
    if (!window.confirm('Are you sure you want to delete this product?')) return;
    try {
      await api.delete(`/products/${productId}`);
      toast.success('Product deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete product');
    }
  };

  const handleProductSaved = () => {
    setShowProductForm(false);
    setEditingProduct(null);
    fetchData();
  };

  const handleEditProduct = (product) => {
    setEditingProduct(product);
    setShowProductForm(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const openOrderDetail = async (order) => {
    setLoadingOrder(true);
    try {
      const response = await api.get(`/admin/orders/${order.order_id}`);
      setSelectedOrder(response.data);
    } catch (error) {
      toast.error('Failed to load order details');
    } finally {
      setLoadingOrder(false);
    }
  };

  const handleOrderStatusUpdate = (updatedOrder) => {
    setSelectedOrder(updatedOrder);
    setOrders(prev => prev.map(o => o.order_id === updatedOrder.order_id ? updatedOrder : o));
  };

  const handleExportExcel = async () => {
    setExporting(true);
    try {
      let params = {};
      if (exportPeriod === 'custom') {
        if (customStartDate) params.start_date = customStartDate;
        if (customEndDate) params.end_date = customEndDate;
      } else {
        params.period = exportPeriod;
      }
      
      const queryStr = new URLSearchParams(params).toString();
      const response = await api.get(`/admin/orders/export/excel?${queryStr}`, { responseType: 'blob' });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = `vastram_orders_${new Date().toISOString().slice(0, 10)}.xlsx`;
      link.click();
      window.URL.revokeObjectURL(url);
      toast.success('Excel exported successfully');
    } catch (error) {
      toast.error('Failed to export orders');
    } finally {
      setExporting(false);
    }
  };

  const handleDownloadInvoice = async (orderId) => {
    try {
      const response = await api.get(`/orders/${orderId}/invoice-pdf`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.download = `Vastram_Invoice_${orderId.slice(0, 8).toUpperCase()}.pdf`;
      link.click();
      window.URL.revokeObjectURL(url);
      toast.success('Invoice PDF downloaded');
    } catch (error) {
      toast.error('Failed to download invoice');
    }
  };

  const handleCreateCoupon = async (e) => {
    e.preventDefault();
    try {
      await api.post('/admin/coupons', {
        ...couponForm,
        code: couponForm.code.toUpperCase(),
        discount_value: parseFloat(couponForm.discount_value),
        min_order: parseFloat(couponForm.min_order || 0),
        max_discount: couponForm.max_discount ? parseFloat(couponForm.max_discount) : null,
        usage_limit: parseInt(couponForm.usage_limit),
      });
      toast.success('Coupon created');
      setShowCouponForm(false);
      setCouponForm({ code: '', discount_type: 'percentage', discount_value: '', min_order: '', max_discount: '', valid_until: '', usage_limit: 100, is_active: true });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create coupon');
    }
  };

  const handleDeleteCoupon = async (couponId) => {
    if (!window.confirm('Delete this coupon?')) return;
    try {
      await api.delete(`/admin/coupons/${couponId}`);
      toast.success('Coupon deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete coupon');
    }
  };

  return (
    <div className="min-h-screen py-12 px-4 sm:px-6 lg:px-8" data-testid="admin-page">
      {authLoading ? (
        <div className="flex items-center justify-center py-24">
          <p className="text-muted-foreground">Loading...</p>
        </div>
      ) : (
      <div className="max-w-7xl mx-auto">
        <h1 className="font-heading text-4xl text-forest mb-8 tracking-tight" data-testid="admin-heading">Admin Panel</h1>

        {/* Tabs */}
        <div className="flex space-x-4 mb-8 border-b border-gold">
          <button
            onClick={() => { setActiveTab('products'); setSelectedOrder(null); }}
            className={`pb-4 px-4 text-sm uppercase tracking-[0.1em] ${activeTab === 'products' ? 'border-b-2 border-forest text-forest' : 'text-muted-foreground'}`}
            data-testid="products-tab"
          >
            Products ({products.length})
          </button>
          <button
            onClick={() => { setActiveTab('orders'); setShowProductForm(false); }}
            className={`pb-4 px-4 text-sm uppercase tracking-[0.1em] ${activeTab === 'orders' ? 'border-b-2 border-forest text-forest' : 'text-muted-foreground'}`}
            data-testid="orders-tab"
          >
            Orders ({orders.length})
          </button>
          <button
            onClick={() => { setActiveTab('users'); setShowProductForm(false); setSelectedOrder(null); }}
            className={`pb-4 px-4 text-sm uppercase tracking-[0.1em] ${activeTab === 'users' ? 'border-b-2 border-forest text-forest' : 'text-muted-foreground'}`}
            data-testid="users-tab"
          >
            Users ({users.length})
          </button>
          <button
            onClick={() => { setActiveTab('coupons'); setShowProductForm(false); setSelectedOrder(null); }}
            className={`pb-4 px-4 text-sm uppercase tracking-[0.1em] ${activeTab === 'coupons' ? 'border-b-2 border-forest text-forest' : 'text-muted-foreground'}`}
            data-testid="coupons-tab"
          >
            Coupons ({coupons.length})
          </button>
          <button
            onClick={() => { setActiveTab('tickets'); setShowProductForm(false); setSelectedOrder(null); }}
            className={`pb-4 px-4 text-sm uppercase tracking-[0.1em] flex items-center space-x-1.5 ${activeTab === 'tickets' ? 'border-b-2 border-forest text-forest' : 'text-muted-foreground'}`}
            data-testid="tickets-tab"
          >
            <HelpCircle className="w-3.5 h-3.5" />
            <span>Tickets ({tickets.length})</span>
          </button>
          <button
            onClick={() => { setActiveTab('reviews'); setShowProductForm(false); setSelectedOrder(null); }}
            className={`pb-4 px-4 text-sm uppercase tracking-[0.1em] flex items-center space-x-1.5 ${activeTab === 'reviews' ? 'border-b-2 border-forest text-forest' : 'text-muted-foreground'}`}
            data-testid="reviews-tab"
          >
            <Star className="w-3.5 h-3.5" />
            <span>Reviews ({allReviews.length})</span>
          </button>
          <button
            onClick={() => { setActiveTab('categories'); setShowProductForm(false); setSelectedOrder(null); }}
            className={`pb-4 px-4 text-sm uppercase tracking-[0.1em] flex items-center space-x-1.5 ${activeTab === 'categories' ? 'border-b-2 border-forest text-forest' : 'text-muted-foreground'}`}
            data-testid="categories-tab"
          >
            <Tag className="w-3.5 h-3.5" />
            <span>Categories</span>
          </button>
          <button
            onClick={() => { setActiveTab('banners'); setShowProductForm(false); setSelectedOrder(null); }}
            className={`pb-4 px-4 text-sm uppercase tracking-[0.1em] flex items-center space-x-1.5 ${activeTab === 'banners' ? 'border-b-2 border-forest text-forest' : 'text-muted-foreground'}`}
            data-testid="banners-tab"
          >
            <Gift className="w-3.5 h-3.5" />
            <span>Banners</span>
          </button>
          <button
            onClick={() => { setActiveTab('stories'); setShowProductForm(false); setSelectedOrder(null); }}
            className={`pb-4 px-4 text-sm uppercase tracking-[0.1em] flex items-center space-x-1.5 ${activeTab === 'stories' ? 'border-b-2 border-forest text-forest' : 'text-muted-foreground'}`}
            data-testid="stories-tab"
          >
            <Edit className="w-3.5 h-3.5" />
            <span>Stories</span>
          </button>
          <button
            onClick={() => { setActiveTab('instagram'); setShowProductForm(false); setSelectedOrder(null); }}
            className={`pb-4 px-4 text-sm uppercase tracking-[0.1em] flex items-center space-x-1.5 ${activeTab === 'instagram' ? 'border-b-2 border-forest text-forest' : 'text-muted-foreground'}`}
            data-testid="instagram-tab"
          >
            <Instagram className="w-3.5 h-3.5" />
            <span>Media</span>
          </button>
          <button
            onClick={() => { setActiveTab('settings'); setShowProductForm(false); setSelectedOrder(null); }}
            className={`pb-4 px-4 text-sm uppercase tracking-[0.1em] flex items-center space-x-1.5 ${activeTab === 'settings' ? 'border-b-2 border-forest text-forest' : 'text-muted-foreground'}`}
            data-testid="settings-tab"
          >
            <span>Site Images</span>
          </button>
        </div>

        {/* Products Tab */}
        {activeTab === 'products' && (
          <div>
            {showProductForm ? (
              <ProductForm
                product={editingProduct}
                onSave={handleProductSaved}
                onCancel={() => { setShowProductForm(false); setEditingProduct(null); }}
                categories={categories}
                allProducts={products}
              />
            ) : (
              <>
                <div className="flex flex-wrap items-end gap-3 mb-6">
                  <button
                    onClick={() => { setShowProductForm(true); setEditingProduct(null); }}
                    className="bg-forest text-ivory px-6 py-3 rounded-none hover:bg-gold hover:text-forest transition-colors text-sm uppercase tracking-[0.1em] flex items-center space-x-2"
                    data-testid="add-product-button"
                  >
                    <Plus className="w-4 h-4" />
                    <span>Add Product</span>
                  </button>
                  <div className="flex-1 min-w-[200px]">
                    <input type="text" value={productSearch} onChange={(e) => setProductSearch(e.target.value)} placeholder="Search products..."
                      className="w-full px-3 py-3 bg-ivory border border-gold/50 rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-forest"
                      data-testid="product-search-input" />
                  </div>
                  <select value={productCategoryFilter} onChange={(e) => setProductCategoryFilter(e.target.value)}
                    className="px-3 py-3 bg-ivory border border-gold/50 rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-forest"
                    data-testid="product-category-filter">
                    <option value="all">All Categories</option>
                    {productCategories.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                  <span className="text-xs text-muted-foreground">{filteredProducts.length} of {products.length}</span>
                </div>
              </>
            )}

            {!showProductForm && (
              <div className="grid grid-cols-1 gap-4">
                {filteredProducts.map((product) => {
                  const stats = productAnalytics[product.product_id] || {};
                  const isExpanded = expandedProduct === product.product_id;
                  return (
                  <div key={product.product_id} className="bg-neutral-warm rounded-sm overflow-hidden" data-testid="admin-product-item">
                    <div className="p-6 flex justify-between items-start">
                      <div className="flex space-x-4 flex-1 min-w-0">
                        <img src={product.images?.[0]} alt={product.name} className="w-20 h-24 object-cover rounded-sm flex-shrink-0" />
                        <div className="flex-1 min-w-0">
                          <h3 className="font-heading text-xl text-forest">{product.name}</h3>
                          <p className="text-sm text-muted-foreground">{product.category} &middot; {product.fabric} &middot; {product.color}</p>
                          <p className="text-forest font-medium">₹{product.price.toLocaleString()}</p>
                          <p className="text-xs text-muted-foreground">Stock: {product.stock} {product.featured && '  |  Featured'}</p>
                          {/* Analytics Summary Badges */}
                          <div className="flex flex-wrap gap-2 mt-2">
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-green-100 text-green-800 text-xs rounded-sm">
                              <ShoppingBag className="w-3 h-3" /> {stats.sales || 0} sold
                            </span>
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-red-100 text-red-800 text-xs rounded-sm">
                              <RotateCcw className="w-3 h-3" /> {stats.returns || 0} returns
                            </span>
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-orange-100 text-orange-800 text-xs rounded-sm">
                              <RefreshCw className="w-3 h-3" /> {stats.exchanges || 0} exchanges
                            </span>
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-blue-100 text-blue-800 text-xs rounded-sm">
                              <Star className="w-3 h-3" /> {stats.reviews || 0} reviews {stats.avg_rating > 0 && `(${stats.avg_rating})`}
                            </span>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center space-x-2 flex-shrink-0 ml-4">
                        <button onClick={() => setExpandedProduct(isExpanded ? null : product.product_id)}
                          className="p-2 text-forest hover:text-gold transition-colors" title="View analytics" data-testid="product-analytics-toggle">
                          {isExpanded ? <ChevronUp className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                        </button>
                        <button onClick={() => handleEditProduct(product)} className="p-2 text-forest hover:text-gold transition-colors" data-testid="edit-product-button">
                          <Edit2 className="w-5 h-5" />
                        </button>
                        <button onClick={() => handleDeleteProduct(product.product_id)} className="p-2 text-forest hover:text-red-500 transition-colors" data-testid="delete-product-button">
                          <Trash2 className="w-5 h-5" />
                        </button>
                      </div>
                    </div>
                    {/* Expanded Analytics Detail */}
                    {isExpanded && (
                      <div className="border-t border-gold/20 p-5 bg-ivory/50" data-testid="product-analytics-detail">
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                          <div className="text-center p-3 bg-green-50 rounded-sm">
                            <p className="font-heading text-2xl text-green-700">{stats.sales || 0}</p>
                            <p className="text-xs text-green-600 uppercase tracking-wider">Units Sold</p>
                          </div>
                          <div className="text-center p-3 bg-red-50 rounded-sm">
                            <p className="font-heading text-2xl text-red-700">{stats.returns || 0}</p>
                            <p className="text-xs text-red-600 uppercase tracking-wider">Returns</p>
                          </div>
                          <div className="text-center p-3 bg-orange-50 rounded-sm">
                            <p className="font-heading text-2xl text-orange-700">{stats.exchanges || 0}</p>
                            <p className="text-xs text-orange-600 uppercase tracking-wider">Exchanges</p>
                          </div>
                          <div className="text-center p-3 bg-blue-50 rounded-sm">
                            <p className="font-heading text-2xl text-blue-700">{stats.avg_rating || '—'}</p>
                            <p className="text-xs text-blue-600 uppercase tracking-wider">Avg Rating ({stats.reviews || 0} reviews)</p>
                          </div>
                        </div>
                        {stats.sales > 0 && stats.returns > 0 && (
                          <p className="text-xs text-muted-foreground">Return rate: {((stats.returns / stats.sales) * 100).toFixed(1)}%</p>
                        )}
                      </div>
                    )}
                  </div>
                  );
                })}
                {filteredProducts.length === 0 && (
                  <p className="text-muted-foreground text-center py-12">{products.length === 0 ? 'No products yet. Add your first product above.' : 'No products match your search.'}</p>
                )}
              </div>
            )}
          </div>
        )}

        {/* Orders Tab */}
        {activeTab === 'orders' && (
          <div>
            {/* Export Controls */}
            {!selectedOrder && !loadingOrder && (
              <div className="bg-neutral-warm p-5 rounded-sm mb-6 flex flex-wrap items-end gap-4" data-testid="export-controls">
                <div>
                  <label className="text-xs uppercase tracking-[0.1em] text-forest block mb-1">Period</label>
                  <select
                    value={exportPeriod}
                    onChange={(e) => setExportPeriod(e.target.value)}
                    className="px-3 py-2 bg-ivory border border-gold/50 rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-forest"
                    data-testid="export-period-select"
                  >
                    <option value="today">Today</option>
                    <option value="week">Last 7 Days</option>
                    <option value="month">Last 30 Days</option>
                    <option value="all">All Time</option>
                    <option value="custom">Custom Dates</option>
                  </select>
                </div>
                {exportPeriod === 'custom' && (
                  <>
                    <div>
                      <label className="text-xs uppercase tracking-[0.1em] text-forest block mb-1">From</label>
                      <input type="date" value={customStartDate} onChange={(e) => setCustomStartDate(e.target.value)}
                        className="px-3 py-2 bg-ivory border border-gold/50 rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-forest"
                        data-testid="export-start-date" />
                    </div>
                    <div>
                      <label className="text-xs uppercase tracking-[0.1em] text-forest block mb-1">To</label>
                      <input type="date" value={customEndDate} onChange={(e) => setCustomEndDate(e.target.value)}
                        className="px-3 py-2 bg-ivory border border-gold/50 rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-forest"
                        data-testid="export-end-date" />
                    </div>
                  </>
                )}
                <button
                  onClick={handleExportExcel}
                  disabled={exporting}
                  className="flex items-center space-x-2 bg-forest text-ivory px-5 py-2 rounded-none hover:bg-gold hover:text-forest transition-colors text-sm uppercase tracking-[0.1em] disabled:opacity-50"
                  data-testid="export-excel-button"
                >
                  <FileSpreadsheet className="w-4 h-4" />
                  <span>{exporting ? 'Exporting...' : 'Export Excel'}</span>
                </button>
              </div>
            )}
            {loadingOrder && (
              <div className="text-center py-12"><p className="text-muted-foreground">Loading order details...</p></div>
            )}

            {selectedOrder && !loadingOrder ? (
              <OrderDetail
                order={selectedOrder}
                onBack={() => setSelectedOrder(null)}
                onStatusUpdate={handleOrderStatusUpdate}
                onDownloadInvoice={handleDownloadInvoice}
              />
            ) : !loadingOrder && (
              <>
                {/* Order Filters */}
                <div className="bg-neutral-warm p-4 rounded-sm mb-4 flex flex-wrap items-end gap-3" data-testid="order-filters">
                  <div>
                    <label className="text-xs uppercase tracking-[0.1em] text-forest block mb-1">
                      <Filter className="w-3 h-3 inline mr-1" />Status
                    </label>
                    <select value={orderStatusFilter} onChange={(e) => setOrderStatusFilter(e.target.value)}
                      className="px-3 py-2 bg-ivory border border-gold/50 rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-forest"
                      data-testid="order-status-filter">
                      <option value="all">All Statuses</option>
                      {ORDER_STATUSES.map(s => <option key={s} value={s}>{STATUS_LABELS[s]}</option>)}
                    </select>
                  </div>
                  <div className="flex-1 min-w-[200px]">
                    <label className="text-xs uppercase tracking-[0.1em] text-forest block mb-1">Search (Name / City / State / Order ID)</label>
                    <input type="text" value={orderSearch} onChange={(e) => setOrderSearch(e.target.value)} placeholder="Type to filter..."
                      className="w-full px-3 py-2 bg-ivory border border-gold/50 rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-forest"
                      data-testid="order-search-input" />
                  </div>
                  <p className="text-xs text-muted-foreground self-center">Showing {filteredOrders.length} of {orders.length}</p>
                </div>

                <div className="space-y-4">
                  {filteredOrders.map((order) => {
                    const isReturnExchange = (order.order_status || '').startsWith('return') || (order.order_status || '').startsWith('exchange');
                    return (
                    <div
                      key={order.order_id}
                      onClick={() => openOrderDetail(order)}
                      className={`bg-neutral-warm p-6 rounded-sm cursor-pointer hover:border-gold border transition-colors group ${isReturnExchange ? 'border-red-300 bg-red-50/30' : 'border-transparent'}`}
                      data-testid="admin-order-item"
                    >
                      <div className="flex justify-between items-start mb-4">
                        <div>
                          <p className="text-sm font-medium text-forest group-hover:text-gold transition-colors">
                            Order #{order.order_id.slice(0, 8)}
                            <ChevronRight className="w-4 h-4 inline ml-1 opacity-0 group-hover:opacity-100 transition-opacity" />
                          </p>
                          <p className="text-xs text-muted-foreground">{new Date(order.created_at).toLocaleString()}</p>
                          <p className="text-xs text-muted-foreground mt-0.5">
                            {order.guest_name || order.customer_info?.name || 'Registered User'} &middot; {order.items.length} item{order.items.length > 1 ? 's' : ''}
                          </p>
                        </div>
                        <div className="flex flex-col items-end gap-1.5">
                          <span className={`px-3 py-1 rounded-full text-xs uppercase tracking-wider ${STATUS_COLORS[order.order_status || 'confirmed']}`}>
                            {STATUS_LABELS[order.order_status || 'confirmed']}
                          </span>
                          <span className={`px-3 py-1 rounded-full text-xs uppercase tracking-wider ${
                            order.payment_status === 'completed' ? 'bg-green-100 text-green-800' :
                            order.payment_status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                            'bg-red-100 text-red-800'
                          }`}>
                            Payment: {order.payment_status}
                          </span>
                          {order.gift_wrap && (
                            <span className="px-3 py-1 rounded-full text-xs uppercase tracking-wider bg-pink-100 text-pink-800 flex items-center">
                              <Gift className="w-3 h-3 mr-1" /> Gift
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="border-t border-gold/20 pt-3 flex justify-between items-center">
                        <div className="flex space-x-4 overflow-hidden">
                          {order.items.slice(0, 3).map((item, idx) => (
                            <span key={idx} className="text-xs text-muted-foreground truncate">{item.name}</span>
                          ))}
                          {order.items.length > 3 && <span className="text-xs text-muted-foreground">+{order.items.length - 3} more</span>}
                        </div>
                        <span className="font-heading text-forest text-lg">₹{order.total_amount?.toLocaleString('en-IN')}
                          <span className="text-xs text-muted-foreground ml-1 font-normal">{order.payment_method === 'cod' ? 'COD' : 'Online'}</span>
                        </span>
                      </div>
                    </div>
                    );
                  })}
                  {filteredOrders.length === 0 && (
                    <p className="text-muted-foreground text-center py-12">{orders.length === 0 ? 'No orders yet.' : 'No orders match filters.'}</p>
                  )}
                </div>
              </>
            )}
          </div>
        )}

        {/* Users Tab */}
        {activeTab === 'users' && (
          <div className="space-y-4" data-testid="users-tab-content">
            {/* User Form Modal */}
            {showUserForm && (
              <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" data-testid="user-form-modal">
                <div className="bg-ivory w-full max-w-lg p-6 space-y-4 max-h-[90vh] overflow-y-auto">
                  <div className="flex justify-between items-center">
                    <h3 className="font-heading text-2xl text-forest">{editingUser ? 'Edit User' : 'Add New User'}</h3>
                    <button onClick={() => { setShowUserForm(false); setEditingUser(null); }} className="text-forest/40 hover:text-forest"><X className="w-5 h-5" /></button>
                  </div>
                  <form onSubmit={handleSaveUser} className="space-y-4">
                    <div>
                      <label className="text-xs uppercase tracking-widest text-forest/60 mb-1 block">Full Name *</label>
                      <input type="text" value={userForm.name} onChange={(e) => setUserForm({...userForm, name: e.target.value})}
                        className="w-full px-4 py-3 bg-transparent border border-forest/20 text-sm text-forest focus:outline-none focus:border-gold"
                        data-testid="user-form-name" placeholder="John Doe" />
                    </div>
                    <div>
                      <label className="text-xs uppercase tracking-widest text-forest/60 mb-1 block">Email *</label>
                      <input type="email" value={userForm.email} onChange={(e) => setUserForm({...userForm, email: e.target.value})}
                        className="w-full px-4 py-3 bg-transparent border border-forest/20 text-sm text-forest focus:outline-none focus:border-gold"
                        data-testid="user-form-email" placeholder="user@example.com" />
                    </div>
                    <div>
                      <label className="text-xs uppercase tracking-widest text-forest/60 mb-1 block">Phone</label>
                      <input type="tel" value={userForm.phone} onChange={(e) => setUserForm({...userForm, phone: e.target.value})}
                        className="w-full px-4 py-3 bg-transparent border border-forest/20 text-sm text-forest focus:outline-none focus:border-gold"
                        data-testid="user-form-phone" placeholder="+91 9876543210" />
                    </div>
                    <div>
                      <label className="text-xs uppercase tracking-widest text-forest/60 mb-1 block">
                        {editingUser ? 'New Password (leave blank to keep current)' : 'Password *'}
                      </label>
                      <input type="password" value={userForm.password} onChange={(e) => setUserForm({...userForm, password: e.target.value})}
                        className="w-full px-4 py-3 bg-transparent border border-forest/20 text-sm text-forest focus:outline-none focus:border-gold"
                        data-testid="user-form-password" placeholder={editingUser ? 'Leave blank to keep unchanged' : 'Min 8 characters'} />
                    </div>
                    <div className="flex items-center space-x-3">
                      <input type="checkbox" id="is-admin" checked={userForm.is_admin} onChange={(e) => setUserForm({...userForm, is_admin: e.target.checked})}
                        className="w-4 h-4 accent-forest" data-testid="user-form-admin-toggle" />
                      <label htmlFor="is-admin" className="text-sm text-forest">Admin privileges</label>
                    </div>
                    <div className="flex gap-3 pt-2">
                      <button type="button" onClick={() => { setShowUserForm(false); setEditingUser(null); }}
                        className="flex-1 border border-forest/20 text-forest py-3 text-sm uppercase tracking-[0.1em] hover:bg-forest/5 transition-colors">
                        Cancel
                      </button>
                      <button type="submit"
                        className="flex-1 bg-forest text-ivory py-3 text-sm uppercase tracking-[0.1em] hover:bg-forest/90 transition-colors"
                        data-testid="user-form-save">
                        {editingUser ? 'Update User' : 'Create User'}
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            )}

            {/* Add User Button + Search */}
            <div className="flex flex-wrap items-end gap-3 mb-4">
              <button onClick={() => openUserForm()} className="bg-forest text-ivory px-5 py-2.5 text-sm uppercase tracking-[0.1em] hover:bg-forest/90 transition-colors flex items-center space-x-2" data-testid="add-user-button">
                <Plus className="w-4 h-4" /> <span>Add User</span>
              </button>
              <a href={`${process.env.REACT_APP_BACKEND_URL}/api/admin/users/export/excel`}
                target="_blank" rel="noopener noreferrer"
                className="bg-gold/20 text-forest px-5 py-2.5 text-sm uppercase tracking-[0.1em] hover:bg-gold/40 transition-colors flex items-center space-x-2"
                data-testid="export-users-button">
                <Download className="w-4 h-4" /> <span>Export</span>
              </a>
              <div className="flex-1 min-w-[200px]">
                <input type="text" value={userSearch} onChange={(e) => setUserSearch(e.target.value)} placeholder="Search by name, email, phone..."
                  className="w-full px-3 py-2.5 bg-ivory border border-gold/50 rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-forest"
                  data-testid="user-search-input" />
              </div>
              <span className="text-xs text-muted-foreground">{filteredUsers.length} of {users.length}</span>
            </div>

            <div className="grid grid-cols-1 gap-4">
              {filteredUsers.map((u) => (
                <div key={u.user_id} className="bg-neutral-warm p-5 rounded-sm" data-testid="admin-user-item">
                  <div className="flex justify-between items-start">
                    <div className="flex items-start space-x-4">
                      <div className="w-12 h-12 rounded-full bg-forest/10 flex items-center justify-center flex-shrink-0">
                        <User className="w-5 h-5 text-forest" />
                      </div>
                      <div>
                        <div className="flex items-center space-x-2">
                          <h3 className="text-forest font-medium">{u.name || 'Unnamed'}</h3>
                          {u.is_admin && <span className="text-xs bg-gold/30 text-forest px-2 py-0.5 rounded-sm">Admin</span>}
                          {u.auth_provider && <span className="text-xs bg-forest/10 text-forest px-2 py-0.5 rounded-sm capitalize">{u.auth_provider}</span>}
                        </div>
                        <p className="text-sm text-muted-foreground">{u.email || 'No email'}</p>
                        {u.phone && <p className="text-sm text-muted-foreground">{u.phone}</p>}
                        {u.gender && <p className="text-xs text-muted-foreground">Gender: {u.gender}</p>}
                        {u.date_of_birth && <p className="text-xs text-muted-foreground">DOB: {u.date_of_birth}</p>}
                        <p className="text-xs text-muted-foreground mt-1">
                          Joined: {new Date(u.created_at).toLocaleDateString()}
                          {u.last_login && ` | Last login: ${new Date(u.last_login).toLocaleDateString()}`}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-start space-x-4">
                      <div className="flex items-center space-x-3 text-sm">
                        <div className="text-center">
                          <p className="font-heading text-xl text-forest">{u.order_count || 0}</p>
                          <p className="text-xs text-muted-foreground">Orders</p>
                        </div>
                        <div className="text-center">
                          <p className="font-heading text-xl text-forest">₹{(u.total_spent || 0).toLocaleString()}</p>
                          <p className="text-xs text-muted-foreground">Spent</p>
                        </div>
                        <div className="text-center">
                          <p className="font-heading text-xl text-forest">{u.login_count || 0}</p>
                          <p className="text-xs text-muted-foreground">Visits</p>
                        </div>
                      </div>
                      <div className="flex items-center space-x-1 ml-3">
                        <button onClick={() => openUserForm(u)} className="p-2 text-forest/40 hover:text-forest hover:bg-forest/5 rounded transition-colors" title="Edit user" data-testid={`edit-user-${u.user_id}`}>
                          <Edit2 className="w-4 h-4" />
                        </button>
                        <button onClick={() => handleDeleteUser(u.user_id, u.name || u.email)} className="p-2 text-forest/40 hover:text-red-600 hover:bg-red-50 rounded transition-colors" title="Delete user" data-testid={`delete-user-${u.user_id}`}>
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
              {filteredUsers.length === 0 && (
                <p className="text-muted-foreground text-center py-12">{users.length === 0 ? 'No users yet.' : 'No users match your search.'}</p>
              )}
            </div>
          </div>
        )}

        {/* Coupons Tab */}
        {activeTab === 'coupons' && (
          <div data-testid="coupons-tab-content">
            {showCouponForm ? (
              <form onSubmit={handleCreateCoupon} className="bg-neutral-warm p-6 rounded-sm mb-6 space-y-4" data-testid="coupon-create-form">
                <div className="flex justify-between items-center">
                  <h3 className="font-heading text-2xl text-forest">Create Coupon</h3>
                  <button type="button" onClick={() => setShowCouponForm(false)} className="p-2 text-forest hover:text-gold">
                    <X className="w-5 h-5" />
                  </button>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="text-xs uppercase tracking-[0.1em] text-forest">Code *</label>
                    <input type="text" required value={couponForm.code}
                      onChange={(e) => setCouponForm({ ...couponForm, code: e.target.value.toUpperCase() })}
                      placeholder="e.g. WELCOME20"
                      className="mt-1 w-full px-4 py-3 bg-ivory border border-gold/50 rounded-sm text-sm uppercase"
                      data-testid="coupon-code-input-admin" />
                  </div>
                  <div>
                    <label className="text-xs uppercase tracking-[0.1em] text-forest">Discount Type</label>
                    <select value={couponForm.discount_type}
                      onChange={(e) => setCouponForm({ ...couponForm, discount_type: e.target.value })}
                      className="mt-1 w-full px-4 py-3 bg-ivory border border-gold/50 rounded-sm text-sm"
                      data-testid="coupon-type-select">
                      <option value="percentage">Percentage (%)</option>
                      <option value="flat">Flat Amount (₹)</option>
                    </select>
                  </div>
                  <div>
                    <label className="text-xs uppercase tracking-[0.1em] text-forest">Discount Value *</label>
                    <input type="number" required min="0" value={couponForm.discount_value}
                      onChange={(e) => setCouponForm({ ...couponForm, discount_value: e.target.value })}
                      placeholder={couponForm.discount_type === 'percentage' ? 'e.g. 20' : 'e.g. 500'}
                      className="mt-1 w-full px-4 py-3 bg-ivory border border-gold/50 rounded-sm text-sm"
                      data-testid="coupon-value-input" />
                  </div>
                  <div>
                    <label className="text-xs uppercase tracking-[0.1em] text-forest">Min Order (₹)</label>
                    <input type="number" min="0" value={couponForm.min_order}
                      onChange={(e) => setCouponForm({ ...couponForm, min_order: e.target.value })}
                      placeholder="0"
                      className="mt-1 w-full px-4 py-3 bg-ivory border border-gold/50 rounded-sm text-sm" />
                  </div>
                  <div>
                    <label className="text-xs uppercase tracking-[0.1em] text-forest">Max Discount (₹)</label>
                    <input type="number" min="0" value={couponForm.max_discount}
                      onChange={(e) => setCouponForm({ ...couponForm, max_discount: e.target.value })}
                      placeholder="Optional"
                      className="mt-1 w-full px-4 py-3 bg-ivory border border-gold/50 rounded-sm text-sm" />
                  </div>
                  <div>
                    <label className="text-xs uppercase tracking-[0.1em] text-forest">Valid Until</label>
                    <input type="date" value={couponForm.valid_until}
                      onChange={(e) => setCouponForm({ ...couponForm, valid_until: e.target.value })}
                      className="mt-1 w-full px-4 py-3 bg-ivory border border-gold/50 rounded-sm text-sm" />
                  </div>
                  <div>
                    <label className="text-xs uppercase tracking-[0.1em] text-forest">Usage Limit</label>
                    <input type="number" min="1" value={couponForm.usage_limit}
                      onChange={(e) => setCouponForm({ ...couponForm, usage_limit: e.target.value })}
                      className="mt-1 w-full px-4 py-3 bg-ivory border border-gold/50 rounded-sm text-sm" />
                  </div>
                </div>
                <button type="submit"
                  className="bg-forest text-ivory px-8 py-3 rounded-none text-sm uppercase tracking-[0.1em] hover:bg-gold hover:text-forest transition-colors"
                  data-testid="coupon-create-submit">
                  Create Coupon
                </button>
              </form>
            ) : (
              <button onClick={() => setShowCouponForm(true)}
                className="mb-6 bg-forest text-ivory px-6 py-3 rounded-none hover:bg-gold hover:text-forest transition-colors text-sm uppercase tracking-[0.1em] flex items-center space-x-2"
                data-testid="add-coupon-button">
                <Tag className="w-4 h-4" />
                <span>Create Coupon</span>
              </button>
            )}

            <div className="space-y-3">
              {/* Coupon Search/Filter */}
              <div className="flex flex-wrap items-center gap-3 mb-2">
                <input type="text" value={couponSearch} onChange={(e) => setCouponSearch(e.target.value)} placeholder="Search by code..."
                  className="flex-1 min-w-[200px] px-3 py-2 bg-ivory border border-gold/50 rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-forest"
                  data-testid="coupon-search-input" />
                {['all', 'active', 'inactive'].map(s => (
                  <button key={s} onClick={() => setCouponStatusFilter(s)}
                    className={`px-3 py-2 text-xs uppercase tracking-[0.1em] rounded-sm ${couponStatusFilter === s ? 'bg-forest text-ivory' : 'bg-neutral-warm text-forest hover:bg-forest/10'}`}
                    data-testid={`coupon-filter-${s}`}>
                    {s}
                  </button>
                ))}
                <span className="text-xs text-muted-foreground">{filteredCoupons.length} of {coupons.length}</span>
              </div>
              {filteredCoupons.map((c) => (
                <div key={c.coupon_id} className="bg-neutral-warm p-5 rounded-sm flex justify-between items-center" data-testid="admin-coupon-item">
                  <div>
                    <div className="flex items-center space-x-3">
                      <span className="font-mono font-medium text-forest bg-gold/20 px-3 py-1 rounded-sm text-sm">{c.code}</span>
                      <span className="text-sm text-forest">
                        {c.discount_type === 'percentage' ? `${c.discount_value}% off` : `₹${c.discount_value} off`}
                      </span>
                      {!c.is_active && <span className="text-xs bg-red-100 text-red-800 px-2 py-0.5 rounded-sm">Inactive</span>}
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">
                      Min: ₹{(c.min_order || 0).toLocaleString()}
                      {c.max_discount ? ` | Max: ₹${c.max_discount.toLocaleString()}` : ''}
                      {` | Used: ${c.used_count || 0}/${c.usage_limit}`}
                      {c.valid_until ? ` | Expires: ${c.valid_until.slice(0, 10)}` : ''}
                    </p>
                  </div>
                  <button onClick={() => handleDeleteCoupon(c.coupon_id)}
                    className="p-2 text-forest hover:text-red-500 transition-colors" data-testid="delete-coupon-button">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
              {filteredCoupons.length === 0 && !showCouponForm && (
                <p className="text-muted-foreground text-center py-12">{coupons.length === 0 ? 'No coupons yet. Create one to get started.' : 'No coupons match your search.'}</p>
              )}
            </div>
          </div>
        )}

        {/* Support Tickets Tab */}
        {activeTab === 'tickets' && (
          <div data-testid="tickets-tab-content">
            {/* Ticket Filters + Search */}
            <div className="flex flex-wrap items-center gap-3 mb-6">
              {['all', 'open', 'resolved'].map(f => (
                <button key={f} onClick={() => setTicketFilter(f)}
                  className={`px-4 py-2 text-xs uppercase tracking-[0.1em] rounded-sm transition-colors ${ticketFilter === f ? 'bg-forest text-ivory' : 'bg-neutral-warm text-forest hover:bg-forest/10'}`}
                  data-testid={`ticket-filter-${f}`}>
                  {f === 'all' ? `All (${tickets.length})` : f === 'open' ? `Open (${tickets.filter(t => t.status === 'open').length})` : `Resolved (${tickets.filter(t => t.status === 'resolved').length})`}
                </button>
              ))}
              <div className="flex-1 min-w-[200px]">
                <input type="text" value={ticketSearch} onChange={(e) => setTicketSearch(e.target.value)} placeholder="Search by title, user, tag..."
                  className="w-full px-3 py-2 bg-ivory border border-gold/50 rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-forest"
                  data-testid="ticket-search-input" />
              </div>
              <span className="text-xs text-muted-foreground">{filteredTickets.length} results</span>
            </div>

            <div className="space-y-4">
              {filteredTickets.map(ticket => (
                <div key={ticket.ticket_id} className={`rounded-sm border overflow-hidden ${ticket.status === 'open' ? 'bg-yellow-50/50 border-yellow-200' : 'bg-green-50/30 border-green-200'}`} data-testid="admin-ticket-item">
                  <div className="p-5">
                    <div className="flex justify-between items-start">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1 flex-wrap">
                          <span className={`px-2.5 py-0.5 rounded-full text-xs uppercase tracking-wider font-medium ${ticket.status === 'open' ? 'bg-yellow-100 text-yellow-800' : 'bg-green-100 text-green-800'}`} data-testid="ticket-status-badge">
                            {ticket.status}
                          </span>
                          <span className="px-2.5 py-0.5 rounded-full text-xs bg-forest/10 text-forest">{ticket.issue_tag}</span>
                          <span className="text-xs text-muted-foreground">#{ticket.ticket_id.slice(0, 8)}</span>
                        </div>
                        <h4 className="text-sm font-medium text-forest mt-1">{ticket.title}</h4>
                        {ticket.description && <p className="text-sm text-muted-foreground mt-1 line-clamp-2">{ticket.description}</p>}
                        {/* Full User Details */}
                        <div className="flex flex-wrap items-center gap-3 mt-2 text-xs">
                          <span className="flex items-center gap-1 text-forest"><User className="w-3 h-3" />{ticket.user_name || 'N/A'}</span>
                          <span className="flex items-center gap-1 text-muted-foreground"><Mail className="w-3 h-3" />{ticket.user_email}</span>
                          {ticket.user_phone && <span className="flex items-center gap-1 text-muted-foreground"><Phone className="w-3 h-3" />{ticket.user_phone}</span>}
                          {ticket.order_id && <span className="text-muted-foreground">Order #{ticket.order_id.slice(0, 8)}</span>}
                          <span className="text-muted-foreground">{new Date(ticket.created_at).toLocaleDateString()}</span>
                        </div>
                      </div>
                      <div className="ml-4 flex-shrink-0 flex items-center gap-2">
                        <button onClick={() => setExpandedTicket(expandedTicket === ticket.ticket_id ? null : ticket.ticket_id)}
                          className="text-xs border border-forest/20 text-forest px-3 py-2 rounded-sm hover:bg-forest/5 transition-colors flex items-center gap-1"
                          data-testid="ticket-expand-btn">
                          <MessageSquare className="w-3 h-3" /> Chat {(ticket.messages?.length || 0) > 0 && `(${ticket.messages.length})`}
                        </button>
                        {ticket.status === 'open' ? (
                          resolvingTicket === ticket.ticket_id ? (
                            <div className="space-y-2 w-56" data-testid="ticket-resolve-form">
                              <textarea value={adminNote} onChange={(e) => setAdminNote(e.target.value)}
                                placeholder="Admin note (optional)..." rows={2}
                                className="w-full px-3 py-2 bg-ivory border border-forest/20 text-sm text-forest focus:outline-none focus:border-gold resize-none rounded-sm"
                                data-testid="ticket-admin-note" />
                              <div className="flex gap-2">
                                <button onClick={() => { setResolvingTicket(null); setAdminNote(''); }}
                                  className="flex-1 text-xs border border-forest/20 text-forest py-1.5 rounded-sm hover:bg-forest/5">Cancel</button>
                                <button onClick={() => handleUpdateTicket(ticket.ticket_id, 'resolved')}
                                  className="flex-1 text-xs bg-green-600 text-white py-1.5 rounded-sm hover:bg-green-700"
                                  data-testid="ticket-confirm-resolve">Resolve</button>
                              </div>
                            </div>
                          ) : (
                            <button onClick={() => setResolvingTicket(ticket.ticket_id)}
                              className="text-xs bg-green-600 text-white px-4 py-2 rounded-sm hover:bg-green-700 transition-colors uppercase tracking-wider"
                              data-testid="ticket-resolve-btn">
                              Resolve
                            </button>
                          )
                        ) : (
                          <button onClick={() => handleUpdateTicket(ticket.ticket_id, 'open')}
                            className="text-xs border border-yellow-400 text-yellow-700 px-4 py-2 rounded-sm hover:bg-yellow-50 transition-colors uppercase tracking-wider"
                            data-testid="ticket-reopen-btn">
                            Reopen
                          </button>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Chat Conversation */}
                  {expandedTicket === ticket.ticket_id && (
                    <div className="border-t border-gold/20 p-5 bg-ivory/50" data-testid="ticket-chat-section">
                      <p className="text-xs uppercase tracking-wider text-gold mb-3">Conversation History</p>
                      <div className="space-y-3 max-h-64 overflow-y-auto mb-3">
                        {/* Initial ticket as first message */}
                        <div className="flex items-start gap-2">
                          <div className="w-6 h-6 rounded-full bg-forest/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                            <User className="w-3 h-3 text-forest" />
                          </div>
                          <div className="flex-1 bg-white p-3 rounded-sm border border-forest/10">
                            <div className="flex items-center gap-2 mb-1">
                              <span className="text-xs font-medium text-forest">{ticket.user_name || 'Customer'}</span>
                              <span className="text-[10px] text-muted-foreground">{new Date(ticket.created_at).toLocaleString()}</span>
                            </div>
                            <p className="text-sm text-forest">{ticket.description || ticket.title}</p>
                          </div>
                        </div>
                        {/* Messages */}
                        {(ticket.messages || []).map(msg => (
                          <div key={msg.message_id} className={`flex items-start gap-2 ${msg.sender === 'admin' ? 'flex-row-reverse' : ''}`}>
                            <div className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 ${msg.sender === 'admin' ? 'bg-gold/20' : 'bg-forest/10'}`}>
                              {msg.sender === 'admin' ? <Star className="w-3 h-3 text-gold" /> : <User className="w-3 h-3 text-forest" />}
                            </div>
                            <div className={`flex-1 p-3 rounded-sm border ${msg.sender === 'admin' ? 'bg-gold/5 border-gold/20' : 'bg-white border-forest/10'}`}>
                              <div className="flex items-center gap-2 mb-1">
                                <span className="text-xs font-medium text-forest">{msg.sender_name}</span>
                                <span className="text-[10px] text-muted-foreground">{new Date(msg.created_at).toLocaleString()}</span>
                              </div>
                              <p className="text-sm text-forest">{msg.text}</p>
                            </div>
                          </div>
                        ))}
                        {(!ticket.messages || ticket.messages.length === 0) && (
                          <p className="text-xs text-muted-foreground text-center py-2">No replies yet</p>
                        )}
                      </div>
                      {/* Admin Reply Input */}
                      <div className="flex gap-2" data-testid="ticket-admin-reply">
                        <input type="text" value={ticketMessage} onChange={(e) => setTicketMessage(e.target.value)}
                          placeholder="Type a reply..."
                          onKeyDown={(e) => e.key === 'Enter' && handleSendTicketMessage(ticket.ticket_id)}
                          className="flex-1 px-3 py-2 bg-white border border-forest/20 text-sm text-forest focus:outline-none focus:border-gold rounded-sm"
                          data-testid="ticket-reply-input" />
                        <button onClick={() => handleSendTicketMessage(ticket.ticket_id)}
                          className="bg-forest text-ivory px-4 py-2 rounded-sm hover:bg-forest/90 transition-colors"
                          data-testid="ticket-send-reply-btn">
                          <Send className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              ))}
              {filteredTickets.length === 0 && (
                <p className="text-muted-foreground text-center py-12">
                  {tickets.length === 0 ? 'No support tickets yet.' : 'No tickets match this filter.'}
                </p>
              )}
            </div>
          </div>
        )}

        {/* Reviews Tab */}
        {activeTab === 'reviews' && (
          <div data-testid="reviews-tab-content">
            {/* Sort controls + Search */}
            <div className="flex flex-wrap items-center gap-3 mb-6">
              {['recent', 'highest', 'lowest'].map(s => (
                <button key={s} onClick={() => setReviewSortBy(s)}
                  className={`px-4 py-2 text-xs uppercase tracking-[0.1em] rounded-sm transition-colors ${reviewSortBy === s ? 'bg-forest text-ivory' : 'bg-neutral-warm text-forest hover:bg-forest/10'}`}
                  data-testid={`review-sort-${s}`}>
                  {s === 'recent' ? 'Most Recent' : s === 'highest' ? 'Highest Rated' : 'Lowest Rated'}
                </button>
              ))}
              <div className="flex-1 min-w-[200px]">
                <input type="text" value={reviewSearch} onChange={(e) => setReviewSearch(e.target.value)} placeholder="Search by product or user..."
                  className="w-full px-3 py-2 bg-ivory border border-gold/50 rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-forest"
                  data-testid="review-search-input" />
              </div>
              <span className="text-xs text-muted-foreground">{filteredReviewsList.length} reviews</span>
            </div>

            {/* Reviews Summary */}
            {allReviews.length > 0 && (
              <div className="bg-neutral-warm p-5 rounded-sm mb-6 grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="reviews-summary">
                <div className="text-center">
                  <p className="font-heading text-3xl text-forest">{allReviews.length}</p>
                  <p className="text-xs text-muted-foreground uppercase tracking-wider">Total Reviews</p>
                </div>
                <div className="text-center">
                  <p className="font-heading text-3xl text-forest">
                    {(allReviews.reduce((sum, r) => sum + (r.product_rating || 0), 0) / allReviews.length).toFixed(1)}
                  </p>
                  <p className="text-xs text-muted-foreground uppercase tracking-wider">Avg Product Rating</p>
                </div>
                <div className="text-center">
                  <p className="font-heading text-3xl text-forest">
                    {(allReviews.reduce((sum, r) => sum + (r.delivery_rating || 0), 0) / allReviews.length).toFixed(1)}
                  </p>
                  <p className="text-xs text-muted-foreground uppercase tracking-wider">Avg Delivery Rating</p>
                </div>
                <div className="text-center">
                  <p className="font-heading text-3xl text-forest">
                    {new Set(allReviews.map(r => r.product_id)).size}
                  </p>
                  <p className="text-xs text-muted-foreground uppercase tracking-wider">Products Reviewed</p>
                </div>
              </div>
            )}

            <div className="space-y-4">
              {[...filteredReviewsList]
                .sort((a, b) => {
                  if (reviewSortBy === 'highest') return (b.product_rating || 0) - (a.product_rating || 0);
                  if (reviewSortBy === 'lowest') return (a.product_rating || 0) - (b.product_rating || 0);
                  return new Date(b.created_at) - new Date(a.created_at);
                })
                .map(review => (
                <div key={review.review_id} className="bg-neutral-warm p-5 rounded-sm" data-testid="admin-review-item">
                  <div className="flex items-start gap-4">
                    {review.product_image && (
                      <img src={review.product_image} alt="" className="w-14 h-18 object-cover rounded-sm flex-shrink-0" />
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between mb-1">
                        <div>
                          <p className="text-sm font-medium text-forest">{review.product_name || 'Product'}</p>
                          <p className="text-xs text-muted-foreground">by {review.user_name || 'Customer'} &middot; {new Date(review.created_at).toLocaleDateString()}</p>
                        </div>
                        <div className="flex items-center gap-3 flex-shrink-0">
                          <div className="text-center">
                            <div className="flex items-center space-x-0.5">
                              {[1,2,3,4,5].map(i => (
                                <Star key={i} className={`w-3 h-3 ${i <= Math.round(review.product_rating) ? (review.product_rating >= 3.5 ? 'text-green-500 fill-current' : review.product_rating >= 2 ? 'text-orange-500 fill-current' : 'text-red-500 fill-current') : 'text-gray-300'}`} />
                              ))}
                            </div>
                            <p className="text-[10px] text-muted-foreground">Product</p>
                          </div>
                          <div className="text-center">
                            <div className="flex items-center space-x-0.5">
                              {[1,2,3,4,5].map(i => (
                                <Star key={i} className={`w-3 h-3 ${i <= Math.round(review.delivery_rating) ? (review.delivery_rating >= 3.5 ? 'text-green-500 fill-current' : review.delivery_rating >= 2 ? 'text-orange-500 fill-current' : 'text-red-500 fill-current') : 'text-gray-300'}`} />
                              ))}
                            </div>
                            <p className="text-[10px] text-muted-foreground">Delivery</p>
                          </div>
                        </div>
                      </div>
                      {review.product_review && <p className="text-sm text-forest mt-2">{review.product_review}</p>}
                      {review.delivery_review && <p className="text-sm text-muted-foreground italic mt-1">Delivery: {review.delivery_review}</p>}
                      {review.media_urls?.length > 0 && (
                        <div className="flex space-x-2 mt-2">
                          {review.media_urls.map((url, i) => (
                            <a key={i} href={url} target="_blank" rel="noopener noreferrer">
                              <img src={url} alt="Review media" className="w-16 h-16 object-cover rounded-sm" />
                            </a>
                          ))}
                        </div>
                      )}
                      {review.order_id && <p className="text-xs text-muted-foreground mt-2">Order #{review.order_id.slice(0, 8)}</p>}
                    </div>
                  </div>
                </div>
              ))}
              {filteredReviewsList.length === 0 && (
                <p className="text-muted-foreground text-center py-12">{allReviews.length === 0 ? 'No reviews yet.' : 'No reviews match your search.'}</p>
              )}
            </div>
          </div>
        )}

        {/* Categories Tab */}
        {activeTab === 'categories' && (
          <div data-testid="categories-tab-content">
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-heading text-xl text-forest">Category Management</h2>
              <button onClick={() => { setShowCategoryForm(true); setEditingCategory(null); setCategoryForm({ name: '', slug: '', description: '', image: '', is_active: true, display_order: 0 }); }}
                className="bg-forest text-ivory px-6 py-3 text-sm uppercase tracking-[0.1em] flex items-center space-x-2 hover:bg-gold hover:text-forest transition-colors"
                data-testid="add-category-button">
                <Plus className="w-4 h-4" /><span>Add Category</span>
              </button>
            </div>

            {showCategoryForm && (
              <div className="bg-neutral-warm p-6 border border-gold/30 mb-6 space-y-4" data-testid="category-form">
                <div className="flex justify-between items-center">
                  <h3 className="font-heading text-lg text-forest">{editingCategory ? 'Edit Category' : 'New Category'}</h3>
                  <button onClick={() => setShowCategoryForm(false)} className="text-forest hover:text-gold"><X className="w-5 h-5" /></button>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs uppercase tracking-[0.1em] text-forest">Name *</label>
                    <input type="text" value={categoryForm.name}
                      onChange={(e) => setCategoryForm({ ...categoryForm, name: e.target.value, slug: editingCategory ? categoryForm.slug : e.target.value.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/(^_|_$)/g, '') })}
                      className="mt-1 w-full px-4 py-3 bg-ivory border border-gold/50 rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-forest"
                      data-testid="category-name-input" />
                  </div>
                  <div>
                    <label className="text-xs uppercase tracking-[0.1em] text-forest">Slug *</label>
                    <input type="text" value={categoryForm.slug}
                      onChange={(e) => setCategoryForm({ ...categoryForm, slug: e.target.value })}
                      className="mt-1 w-full px-4 py-3 bg-ivory border border-gold/50 rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-forest"
                      data-testid="category-slug-input" />
                  </div>
                </div>
                <div>
                  <label className="text-xs uppercase tracking-[0.1em] text-forest">Description</label>
                  <textarea value={categoryForm.description}
                    onChange={(e) => setCategoryForm({ ...categoryForm, description: e.target.value })}
                    rows={2} className="mt-1 w-full px-4 py-3 bg-ivory border border-gold/50 rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-forest resize-none"
                    data-testid="category-description-input" />
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className="text-xs uppercase tracking-[0.1em] text-forest">Image URL</label>
                    <input type="text" value={categoryForm.image}
                      onChange={(e) => setCategoryForm({ ...categoryForm, image: e.target.value })}
                      className="mt-1 w-full px-4 py-3 bg-ivory border border-gold/50 rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-forest" />
                  </div>
                  <div>
                    <label className="text-xs uppercase tracking-[0.1em] text-forest">Display Order</label>
                    <input type="number" value={categoryForm.display_order}
                      onChange={(e) => setCategoryForm({ ...categoryForm, display_order: parseInt(e.target.value) || 0 })}
                      className="mt-1 w-full px-4 py-3 bg-ivory border border-gold/50 rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-forest" />
                  </div>
                  <div className="flex items-end pb-1">
                    <label className="flex items-center space-x-3 cursor-pointer">
                      <input type="checkbox" checked={categoryForm.is_active}
                        onChange={(e) => setCategoryForm({ ...categoryForm, is_active: e.target.checked })}
                        className="w-4 h-4 accent-forest" />
                      <span className="text-sm text-forest">Active</span>
                    </label>
                  </div>
                </div>
                <button onClick={async () => {
                  if (!categoryForm.name || !categoryForm.slug) { toast.error('Name and slug required'); return; }
                  try {
                    if (editingCategory) {
                      await api.put(`/categories/${editingCategory.category_id}`, categoryForm);
                      toast.success('Category updated');
                    } else {
                      await api.post('/categories', categoryForm);
                      toast.success('Category created');
                    }
                    setShowCategoryForm(false);
                    fetchData();
                  } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
                }} className="bg-forest text-ivory px-8 py-3 text-sm uppercase tracking-[0.1em] hover:bg-gold hover:text-forest transition-colors"
                  data-testid="save-category-button">
                  {editingCategory ? 'Update' : 'Create'} Category
                </button>
              </div>
            )}

            <div className="space-y-3">
              {categories.map(cat => (
                <div key={cat.category_id} className="flex items-center justify-between p-4 bg-neutral-warm border border-gold/20" data-testid={`category-row-${cat.category_id}`}>
                  <div className="flex items-center space-x-4">
                    {cat.image && <img src={cat.image} alt="" className="w-12 h-12 object-cover rounded-sm" />}
                    <div>
                      <p className="text-sm font-medium text-forest">{cat.name}</p>
                      <p className="text-xs text-muted-foreground">slug: {cat.slug} · order: {cat.display_order}</p>
                      {cat.description && <p className="text-xs text-muted-foreground mt-0.5">{cat.description}</p>}
                    </div>
                  </div>
                  <div className="flex items-center space-x-3">
                    <span className={`text-xs px-2 py-1 rounded-sm ${cat.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>
                      {cat.is_active ? 'Active' : 'Inactive'}
                    </span>
                    <button onClick={() => { setEditingCategory(cat); setCategoryForm({ name: cat.name, slug: cat.slug, description: cat.description || '', image: cat.image || '', is_active: cat.is_active, display_order: cat.display_order || 0 }); setShowCategoryForm(true); }}
                      className="p-2 text-forest hover:text-gold transition-colors" data-testid={`edit-category-${cat.category_id}`}>
                      <Edit2 className="w-4 h-4" />
                    </button>
                    <button onClick={async () => {
                      if (!window.confirm(`Delete category "${cat.name}"?`)) return;
                      try { await api.delete(`/categories/${cat.category_id}`); toast.success('Deleted'); fetchData(); } catch { toast.error('Failed'); }
                    }} className="p-2 text-forest hover:text-red-500 transition-colors" data-testid={`delete-category-${cat.category_id}`}>
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))}
              {categories.length === 0 && <p className="text-center text-muted-foreground py-8">No categories yet.</p>}
            </div>
          </div>
        )}

        {/* Banners Tab */}
        {activeTab === 'banners' && (
          <div data-testid="banners-tab-content">
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-heading text-xl text-forest">Sale & Discount Banners</h2>
              <button onClick={() => { setShowBannerForm(true); setEditingBanner(null); setBannerForm({ title: '', subtitle: '', image_url: '', link_url: '', bg_color: '#2D4A3E', text_color: '#FFFFFF', is_active: true, display_order: 0, pages: ['home'] }); }}
                className="bg-forest text-ivory px-6 py-3 text-sm uppercase tracking-[0.1em] flex items-center space-x-2 hover:bg-gold hover:text-forest transition-colors"
                data-testid="add-banner-button">
                <Plus className="w-4 h-4" /><span>Add Banner</span>
              </button>
            </div>

            {showBannerForm && (
              <div className="bg-neutral-warm p-6 border border-gold/30 mb-6 space-y-4" data-testid="banner-form">
                <div className="flex justify-between items-center">
                  <h3 className="font-heading text-lg text-forest">{editingBanner ? 'Edit Banner' : 'New Banner'}</h3>
                  <button onClick={() => setShowBannerForm(false)} className="text-forest hover:text-gold"><X className="w-5 h-5" /></button>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs uppercase tracking-[0.1em] text-forest">Title *</label>
                    <input type="text" value={bannerForm.title}
                      onChange={(e) => setBannerForm({ ...bannerForm, title: e.target.value })}
                      placeholder="e.g. FLAT 20% OFF"
                      className="mt-1 w-full px-4 py-3 bg-ivory border border-gold/50 rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-forest"
                      data-testid="banner-title-input" />
                  </div>
                  <div>
                    <label className="text-xs uppercase tracking-[0.1em] text-forest">Subtitle</label>
                    <input type="text" value={bannerForm.subtitle}
                      onChange={(e) => setBannerForm({ ...bannerForm, subtitle: e.target.value })}
                      placeholder="e.g. Use code WELCOME20"
                      className="mt-1 w-full px-4 py-3 bg-ivory border border-gold/50 rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-forest"
                      data-testid="banner-subtitle-input" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs uppercase tracking-[0.1em] text-forest">Link URL</label>
                    <input type="text" value={bannerForm.link_url}
                      onChange={(e) => setBannerForm({ ...bannerForm, link_url: e.target.value })}
                      placeholder="/products or /products?category=saree"
                      className="mt-1 w-full px-4 py-3 bg-ivory border border-gold/50 rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-forest" />
                  </div>
                  <div>
                    <label className="text-xs uppercase tracking-[0.1em] text-forest">Display Order</label>
                    <input type="number" value={bannerForm.display_order}
                      onChange={(e) => setBannerForm({ ...bannerForm, display_order: parseInt(e.target.value) || 0 })}
                      className="mt-1 w-full px-4 py-3 bg-ivory border border-gold/50 rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-forest" />
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className="text-xs uppercase tracking-[0.1em] text-forest">Background Color</label>
                    <div className="flex items-center space-x-2 mt-1">
                      <input type="color" value={bannerForm.bg_color}
                        onChange={(e) => setBannerForm({ ...bannerForm, bg_color: e.target.value })}
                        className="w-10 h-10 cursor-pointer border border-gold/50" />
                      <input type="text" value={bannerForm.bg_color}
                        onChange={(e) => setBannerForm({ ...bannerForm, bg_color: e.target.value })}
                        className="flex-1 px-4 py-3 bg-ivory border border-gold/50 rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-forest" />
                    </div>
                  </div>
                  <div>
                    <label className="text-xs uppercase tracking-[0.1em] text-forest">Text Color</label>
                    <div className="flex items-center space-x-2 mt-1">
                      <input type="color" value={bannerForm.text_color}
                        onChange={(e) => setBannerForm({ ...bannerForm, text_color: e.target.value })}
                        className="w-10 h-10 cursor-pointer border border-gold/50" />
                      <input type="text" value={bannerForm.text_color}
                        onChange={(e) => setBannerForm({ ...bannerForm, text_color: e.target.value })}
                        className="flex-1 px-4 py-3 bg-ivory border border-gold/50 rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-forest" />
                    </div>
                  </div>
                  <div className="flex items-end pb-1">
                    <label className="flex items-center space-x-3 cursor-pointer">
                      <input type="checkbox" checked={bannerForm.is_active}
                        onChange={(e) => setBannerForm({ ...bannerForm, is_active: e.target.checked })}
                        className="w-4 h-4 accent-forest" />
                      <span className="text-sm text-forest">Active</span>
                    </label>
                  </div>
                </div>
                <div>
                  <label className="text-xs uppercase tracking-[0.1em] text-forest">Show on Pages</label>
                  <div className="flex flex-wrap gap-3 mt-2">
                    {['home', 'products', 'product_detail'].map(pg => (
                      <label key={pg} className="flex items-center space-x-2 cursor-pointer">
                        <input type="checkbox" checked={bannerForm.pages.includes(pg)}
                          onChange={(e) => {
                            const pages = e.target.checked
                              ? [...bannerForm.pages, pg]
                              : bannerForm.pages.filter(p => p !== pg);
                            setBannerForm({ ...bannerForm, pages });
                          }}
                          className="w-4 h-4 accent-forest" />
                        <span className="text-sm capitalize">{pg.replace('_', ' ')}</span>
                      </label>
                    ))}
                  </div>
                </div>
                <div>
                  <label className="text-xs uppercase tracking-[0.1em] text-forest mb-2 block">Preview</label>
                  <div className="py-3 px-4 text-center" style={{ backgroundColor: bannerForm.bg_color, color: bannerForm.text_color }}>
                    <span className="text-sm font-medium tracking-wide">{bannerForm.title || 'Banner Title'}</span>
                    {bannerForm.subtitle && <span className="text-sm ml-3 opacity-90">| {bannerForm.subtitle}</span>}
                  </div>
                </div>
                <button onClick={async () => {
                  if (!bannerForm.title) { toast.error('Title required'); return; }
                  try {
                    if (editingBanner) {
                      await api.put(`/banners/${editingBanner.banner_id}`, bannerForm);
                      toast.success('Banner updated');
                    } else {
                      await api.post('/banners', bannerForm);
                      toast.success('Banner created');
                    }
                    setShowBannerForm(false);
                    fetchData();
                  } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
                }} className="bg-forest text-ivory px-8 py-3 text-sm uppercase tracking-[0.1em] hover:bg-gold hover:text-forest transition-colors"
                  data-testid="save-banner-button">
                  {editingBanner ? 'Update' : 'Create'} Banner
                </button>
              </div>
            )}

            <div className="space-y-3">
              {banners.map(banner => (
                <div key={banner.banner_id} className="border border-gold/20 overflow-hidden" data-testid={`banner-row-${banner.banner_id}`}>
                  <div className="py-2 px-4 text-center text-sm" style={{ backgroundColor: banner.bg_color, color: banner.text_color }}>
                    <span className="font-medium">{banner.title}</span>
                    {banner.subtitle && <span className="ml-2 opacity-80">| {banner.subtitle}</span>}
                  </div>
                  <div className="flex items-center justify-between p-3 bg-neutral-warm">
                    <div className="text-xs text-muted-foreground">
                      Pages: {banner.pages?.join(', ')} · Order: {banner.display_order} {banner.link_url && <span>· Links to: {banner.link_url}</span>}
                    </div>
                    <div className="flex items-center space-x-3">
                      <span className={`text-xs px-2 py-1 rounded-sm ${banner.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>
                        {banner.is_active ? 'Active' : 'Inactive'}
                      </span>
                      <button onClick={() => { setEditingBanner(banner); setBannerForm({ title: banner.title, subtitle: banner.subtitle || '', image_url: banner.image_url || '', link_url: banner.link_url || '', bg_color: banner.bg_color || '#2D4A3E', text_color: banner.text_color || '#FFFFFF', is_active: banner.is_active, display_order: banner.display_order || 0, pages: banner.pages || ['home'] }); setShowBannerForm(true); }}
                        className="p-2 text-forest hover:text-gold transition-colors" data-testid={`edit-banner-${banner.banner_id}`}>
                        <Edit2 className="w-4 h-4" />
                      </button>
                      <button onClick={async () => {
                        if (!window.confirm('Delete this banner?')) return;
                        try { await api.delete(`/banners/${banner.banner_id}`); toast.success('Deleted'); fetchData(); } catch { toast.error('Failed'); }
                      }} className="p-2 text-forest hover:text-red-500 transition-colors" data-testid={`delete-banner-${banner.banner_id}`}>
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
              {banners.length === 0 && <p className="text-center text-muted-foreground py-8">No banners yet. Create your first sale banner!</p>}
            </div>
          </div>
        )}

        {/* ========== Stories Tab ========== */}
        {activeTab === 'stories' && (
          <div>
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-heading text-xl text-forest">Drape Stories</h2>
              <button
                onClick={() => { setShowStoryForm(true); setEditingStory(null); setStoryForm({ title: '', excerpt: '', content: '', author: 'Vastram', image_url: '', tags: '', is_published: true }); }}
                className="bg-forest text-ivory px-6 py-3 text-sm uppercase tracking-[0.1em] hover:bg-gold hover:text-forest transition-colors flex items-center space-x-2"
                data-testid="add-story-button"
              >
                <Plus className="w-4 h-4" /><span>Add Story</span>
              </button>
            </div>

            {showStoryForm && (
              <div className="bg-neutral-warm p-6 mb-6 space-y-4" data-testid="story-form">
                <div className="flex items-center justify-between">
                  <h3 className="font-heading text-lg text-forest">{editingStory ? 'Edit Story' : 'New Story'}</h3>
                  <button onClick={() => setShowStoryForm(false)} className="text-forest hover:text-red-500"><X className="w-5 h-5" /></button>
                </div>
                <input type="text" placeholder="Title" value={storyForm.title}
                  onChange={e => setStoryForm({ ...storyForm, title: e.target.value })}
                  className="w-full px-3 py-3 bg-ivory border border-gold/50 text-sm focus:outline-none focus:ring-2 focus:ring-forest"
                  data-testid="story-title-input" />
                <input type="text" placeholder="Excerpt (short summary)" value={storyForm.excerpt}
                  onChange={e => setStoryForm({ ...storyForm, excerpt: e.target.value })}
                  className="w-full px-3 py-3 bg-ivory border border-gold/50 text-sm focus:outline-none focus:ring-2 focus:ring-forest"
                  data-testid="story-excerpt-input" />
                <textarea placeholder="Full content..." value={storyForm.content}
                  onChange={e => setStoryForm({ ...storyForm, content: e.target.value })}
                  rows={6}
                  className="w-full px-3 py-3 bg-ivory border border-gold/50 text-sm focus:outline-none focus:ring-2 focus:ring-forest"
                  data-testid="story-content-input" />
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <input type="text" placeholder="Author" value={storyForm.author}
                    onChange={e => setStoryForm({ ...storyForm, author: e.target.value })}
                    className="px-3 py-3 bg-ivory border border-gold/50 text-sm focus:outline-none focus:ring-2 focus:ring-forest"
                    data-testid="story-author-input" />
                  <input type="text" placeholder="Image URL" value={storyForm.image_url}
                    onChange={e => setStoryForm({ ...storyForm, image_url: e.target.value })}
                    className="px-3 py-3 bg-ivory border border-gold/50 text-sm focus:outline-none focus:ring-2 focus:ring-forest"
                    data-testid="story-image-input" />
                  <input type="text" placeholder="Tags (comma separated)" value={storyForm.tags}
                    onChange={e => setStoryForm({ ...storyForm, tags: e.target.value })}
                    className="px-3 py-3 bg-ivory border border-gold/50 text-sm focus:outline-none focus:ring-2 focus:ring-forest"
                    data-testid="story-tags-input" />
                </div>
                <label className="flex items-center space-x-2 cursor-pointer">
                  <input type="checkbox" checked={storyForm.is_published}
                    onChange={e => setStoryForm({ ...storyForm, is_published: e.target.checked })}
                    className="w-4 h-4 accent-forest" />
                  <span className="text-sm text-forest">Published</span>
                </label>
                <button onClick={async () => {
                  if (!storyForm.title.trim()) { toast.error('Title is required'); return; }
                  try {
                    const payload = { ...storyForm, tags: storyForm.tags ? storyForm.tags.split(',').map(t => t.trim()).filter(Boolean) : [] };
                    if (editingStory) {
                      await api.put(`/admin/drape-stories/${editingStory.story_id}`, payload);
                      toast.success('Story updated');
                    } else {
                      await api.post('/admin/drape-stories', payload);
                      toast.success('Story created');
                    }
                    setShowStoryForm(false);
                    fetchData();
                  } catch (e) { toast.error(e.response?.data?.detail || 'Failed to save story'); }
                }} className="bg-forest text-ivory px-8 py-3 text-sm uppercase tracking-[0.1em] hover:bg-gold hover:text-forest transition-colors"
                  data-testid="save-story-button">
                  {editingStory ? 'Update' : 'Create'} Story
                </button>
              </div>
            )}

            <div className="space-y-4">
              {stories.map(story => (
                <div key={story.story_id} className="border border-gold/20 bg-neutral-warm overflow-hidden" data-testid={`story-row-${story.story_id}`}>
                  <div className="flex items-start gap-4 p-4">
                    {story.image_url && <img src={story.image_url} alt="" className="w-20 h-20 object-cover rounded-sm flex-shrink-0" />}
                    <div className="flex-1 min-w-0">
                      <h4 className="font-heading text-forest text-base truncate">{story.title}</h4>
                      <p className="text-sm text-muted-foreground line-clamp-2 mt-1">{story.excerpt || story.content?.slice(0, 120)}</p>
                      <div className="flex items-center gap-3 mt-2 text-xs text-muted-foreground">
                        <span>By {story.author}</span>
                        <span className={`px-2 py-0.5 rounded-sm ${story.is_published ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>
                          {story.is_published ? 'Published' : 'Draft'}
                        </span>
                        {story.tags?.length > 0 && <span>{story.tags.join(', ')}</span>}
                      </div>
                    </div>
                    <div className="flex items-center space-x-2 flex-shrink-0">
                      <button onClick={() => {
                        setEditingStory(story);
                        setStoryForm({ title: story.title, excerpt: story.excerpt || '', content: story.content || '', author: story.author || 'Vastram', image_url: story.image_url || '', tags: (story.tags || []).join(', '), is_published: story.is_published });
                        setShowStoryForm(true);
                      }} className="p-2 text-forest hover:text-gold" data-testid={`edit-story-${story.story_id}`}>
                        <Edit2 className="w-4 h-4" />
                      </button>
                      <button onClick={async () => {
                        if (!window.confirm('Delete this story?')) return;
                        try { await api.delete(`/admin/drape-stories/${story.story_id}`); toast.success('Story deleted'); fetchData(); } catch { toast.error('Failed'); }
                      }} className="p-2 text-forest hover:text-red-500" data-testid={`delete-story-${story.story_id}`}>
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
              {stories.length === 0 && <p className="text-center text-muted-foreground py-8">No stories yet. Share the first Drape Story!</p>}
            </div>
          </div>
        )}

        {/* ========== Instagram Tab ========== */}
        {activeTab === 'instagram' && (
          <div>
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-heading text-xl text-forest">Media Gallery</h2>
              <button
                onClick={() => { setShowInstaForm(true); setEditingInstaPost(null); setInstaForm({ media_type: 'image', image_url: '', youtube_url: '', caption: '', instagram_url: '', display_order: 0, is_active: true }); }}
                className="bg-forest text-ivory px-6 py-3 text-sm uppercase tracking-[0.1em] hover:bg-gold hover:text-forest transition-colors flex items-center space-x-2"
                data-testid="add-instagram-button"
              >
                <Plus className="w-4 h-4" /><span>Add Media</span>
              </button>
            </div>

            {showInstaForm && (
              <div className="bg-neutral-warm p-6 mb-6 space-y-4" data-testid="instagram-form">
                <div className="flex items-center justify-between">
                  <h3 className="font-heading text-lg text-forest">{editingInstaPost ? 'Edit Media' : 'New Media'}</h3>
                  <button onClick={() => setShowInstaForm(false)} className="text-forest hover:text-red-500"><X className="w-5 h-5" /></button>
                </div>
                {/* Media Type Toggle */}
                <div className="flex gap-2">
                  {['image', 'youtube'].map(t => (
                    <button key={t} onClick={() => setInstaForm({ ...instaForm, media_type: t })}
                      className={`px-4 py-2 text-sm uppercase tracking-[0.1em] border transition-colors ${instaForm.media_type === t ? 'border-forest bg-forest text-ivory' : 'border-gold/40 text-forest hover:bg-gold/10'}`}
                      data-testid={`media-type-${t}`}>
                      {t === 'image' ? 'Image' : 'YouTube Video'}
                    </button>
                  ))}
                </div>
                {instaForm.media_type === 'image' ? (
                  <div className="space-y-2">
                    <input type="text" placeholder="Image URL (direct link, Google Drive, etc.)" value={instaForm.image_url}
                      onChange={e => setInstaForm({ ...instaForm, image_url: e.target.value })}
                      className="w-full px-3 py-3 bg-ivory border border-gold/50 text-sm focus:outline-none focus:ring-2 focus:ring-forest"
                      data-testid="insta-image-input" />
                    <p className="text-xs text-muted-foreground">Supports: Direct image URLs, Google Drive share links</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <input type="text" placeholder="YouTube Video URL" value={instaForm.youtube_url}
                      onChange={e => setInstaForm({ ...instaForm, youtube_url: e.target.value })}
                      className="w-full px-3 py-3 bg-ivory border border-gold/50 text-sm focus:outline-none focus:ring-2 focus:ring-forest"
                      data-testid="insta-youtube-input" />
                    <p className="text-xs text-muted-foreground">Paste any YouTube link (youtube.com/watch, youtu.be, shorts)</p>
                    <input type="text" placeholder="Custom thumbnail URL (optional — auto-fetched from YouTube)" value={instaForm.image_url}
                      onChange={e => setInstaForm({ ...instaForm, image_url: e.target.value })}
                      className="w-full px-3 py-3 bg-ivory border border-gold/50 text-sm focus:outline-none focus:ring-2 focus:ring-forest" />
                  </div>
                )}
                <input type="text" placeholder="Caption" value={instaForm.caption}
                  onChange={e => setInstaForm({ ...instaForm, caption: e.target.value })}
                  className="w-full px-3 py-3 bg-ivory border border-gold/50 text-sm focus:outline-none focus:ring-2 focus:ring-forest"
                  data-testid="insta-caption-input" />
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <input type="text" placeholder="Instagram/Source URL (optional)" value={instaForm.instagram_url}
                    onChange={e => setInstaForm({ ...instaForm, instagram_url: e.target.value })}
                    className="px-3 py-3 bg-ivory border border-gold/50 text-sm focus:outline-none focus:ring-2 focus:ring-forest"
                    data-testid="insta-url-input" />
                  <input type="number" placeholder="Display Order" value={instaForm.display_order}
                    onChange={e => setInstaForm({ ...instaForm, display_order: parseInt(e.target.value) || 0 })}
                    className="px-3 py-3 bg-ivory border border-gold/50 text-sm focus:outline-none focus:ring-2 focus:ring-forest"
                    data-testid="insta-order-input" />
                </div>
                <label className="flex items-center space-x-2 cursor-pointer">
                  <input type="checkbox" checked={instaForm.is_active}
                    onChange={e => setInstaForm({ ...instaForm, is_active: e.target.checked })}
                    className="w-4 h-4 accent-forest" />
                  <span className="text-sm text-forest">Active</span>
                </label>
                <button onClick={async () => {
                  if (instaForm.media_type === 'image' && !instaForm.image_url.trim()) { toast.error('Image URL is required'); return; }
                  if (instaForm.media_type === 'youtube' && !instaForm.youtube_url.trim()) { toast.error('YouTube URL is required'); return; }
                  try {
                    if (editingInstaPost) {
                      await api.put(`/admin/instagram-posts/${editingInstaPost.post_id}`, instaForm);
                      toast.success('Media updated');
                    } else {
                      await api.post('/admin/instagram-posts', instaForm);
                      toast.success('Media created');
                    }
                    setShowInstaForm(false);
                    fetchData();
                  } catch (e) { toast.error(e.response?.data?.detail || 'Failed to save'); }
                }} className="bg-forest text-ivory px-8 py-3 text-sm uppercase tracking-[0.1em] hover:bg-gold hover:text-forest transition-colors"
                  data-testid="save-instagram-button">
                  {editingInstaPost ? 'Update' : 'Create'} Media
                </button>
              </div>
            )}

            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
              {instagramPosts.map(post => (
                <div key={post.post_id} className="border border-gold/20 bg-neutral-warm overflow-hidden group" data-testid={`insta-row-${post.post_id}`}>
                  <div className="aspect-square relative">
                    <img src={post.image_url} alt={post.caption} className="w-full h-full object-cover" />
                    {post.media_type === 'youtube' && (
                      <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                        <div className="w-12 h-12 bg-red-600 rounded-full flex items-center justify-center shadow-lg">
                          <svg className="w-5 h-5 text-white ml-0.5" fill="currentColor" viewBox="0 0 24 24"><path d="M8 5v14l11-7z"/></svg>
                        </div>
                      </div>
                    )}
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-colors flex items-center justify-center opacity-0 group-hover:opacity-100">
                      <div className="flex items-center space-x-2">
                        <button onClick={() => {
                          setEditingInstaPost(post);
                          setInstaForm({ media_type: post.media_type || 'image', image_url: post.image_url, youtube_url: post.youtube_url || '', caption: post.caption || '', instagram_url: post.instagram_url || '', display_order: post.display_order || 0, is_active: post.is_active });
                          setShowInstaForm(true);
                        }} className="p-2 bg-white/90 rounded-full text-forest hover:bg-gold" data-testid={`edit-insta-${post.post_id}`}>
                          <Edit2 className="w-4 h-4" />
                        </button>
                        <button onClick={async () => {
                          if (!window.confirm('Delete this post?')) return;
                          try { await api.delete(`/admin/instagram-posts/${post.post_id}`); toast.success('Post deleted'); fetchData(); } catch { toast.error('Failed'); }
                        }} className="p-2 bg-white/90 rounded-full text-red-500 hover:bg-red-100" data-testid={`delete-insta-${post.post_id}`}>
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                  <div className="p-2">
                    <p className="text-xs text-muted-foreground truncate">{post.caption || 'No caption'}</p>
                    <div className="flex items-center justify-between mt-1">
                      <span className="text-[10px] text-muted-foreground">{post.media_type === 'youtube' ? 'YT' : 'IMG'} #{post.display_order}</span>
                      <span className={`text-[10px] px-1.5 py-0.5 rounded-sm ${post.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>
                        {post.is_active ? 'Active' : 'Hidden'}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            {instagramPosts.length === 0 && <p className="text-center text-muted-foreground py-8">No media yet. Add Instagram posts or YouTube videos to showcase on the homepage!</p>}
          </div>
        )}

        {/* ========== Site Images Tab ========== */}
        {activeTab === 'settings' && (
          <div>
            <h2 className="font-heading text-xl text-forest mb-6">Website Images</h2>
            <p className="text-sm text-muted-foreground mb-6">Update images across the website. Supports Google Drive links (auto-converted).</p>
            <div className="space-y-6 max-w-2xl">
              {[
                { key: 'hero_image', label: 'Home Page Hero Banner', desc: 'Main banner image on the homepage (recommended: 1920x1080)' },
                { key: 'saree_section_image', label: 'Home Page Sarees Section', desc: 'Image for the Sarees showcase on homepage' },
                { key: 'suit_section_image', label: 'Home Page Suits Section', desc: 'Image for the Suits showcase on homepage' },
                { key: 'our_story_image', label: 'Our Story Page Image', desc: 'Image on the About/Our Story page' },
              ].map(({ key, label, desc }) => (
                <div key={key} className="bg-neutral-warm p-5 rounded-sm">
                  <label className="text-xs uppercase tracking-[0.1em] text-forest block mb-1">{label}</label>
                  <p className="text-xs text-muted-foreground mb-2">{desc}</p>
                  <input
                    type="text" value={settingsForm[key]}
                    onChange={e => setSettingsForm({ ...settingsForm, [key]: e.target.value })}
                    placeholder="https://drive.google.com/file/d/.../view or direct image URL"
                    className="w-full px-3 py-3 bg-ivory border border-gold/50 text-sm focus:outline-none focus:ring-2 focus:ring-forest"
                    data-testid={`setting-${key}`}
                  />
                  {settingsForm[key] && (
                    <img src={settingsForm[key]} alt={label} className="mt-2 h-20 object-cover rounded-sm border border-gold/20"
                      onError={e => { e.target.style.display = 'none'; }} />
                  )}
                </div>
              ))}
              <button
                onClick={async () => {
                  setSavingSettings(true);
                  try {
                    await api.put('/site-settings', settingsForm);
                    toast.success('Site images updated');
                    fetchData();
                  } catch (e) { toast.error(e.response?.data?.detail || 'Failed to save'); }
                  finally { setSavingSettings(false); }
                }}
                disabled={savingSettings}
                className="bg-forest text-ivory px-8 py-3 text-sm uppercase tracking-[0.1em] hover:bg-gold hover:text-forest transition-colors disabled:opacity-50"
                data-testid="save-settings-button"
              >
                {savingSettings ? 'Saving...' : 'Save Images'}
              </button>
            </div>
          </div>
        )}
      </div>
      )}
    </div>
  );
};

export default Admin;
